// trsting the branch

import CS2 from "canvas2svg";

import "./Diagram.css";
import { useQueryClient } from "@tanstack/react-query";
import closeIcon from "../../assets/icons/close.svg";
import saveIcon from "../../assets/icons/check.svg";
import trashIcon from "../../assets/icons/trash.svg";
import itemsIcon from "../../assets/icons/items.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  isLineParallelToTop,
  isLineParallelToSide,
  calculateDistance,
  isLineNearPoint,
} from "../../utils/constants";
import DownspoutModal from "../DownspoutModal/DownspoutModal";
import { OverwriteDiagramModal } from "../OverwriteDiagramModal/OverwriteDiagramModal";
import { AnnotationModal } from "../AnnotationModal/AnnotationModal";
import { useProductsCatalog } from "../../contexts/ProductsContext";
import { useProductsListed, useProductsPricing } from "../../hooks/useProducts";
import {
  catalogItemToTool,
  isCatalogItemDrawable,
} from "../../utils/catalogToolAdapter";
import { useOnboarding } from "../../onboarding/OnboardingContext";

// ---- fixed palette used by free tools ----
const COMMON_COLORS = [
  "#ffffff", // white (visible on dark)
  "#ff0000",
  "#00aaff",
  "#00c853",
  "#9c27b0",
  "#000000",
];

// ------- small helpers -------
// Use green only for the IN-PROGRESS preview when perfectly straight.
// Never persist green into line.color.

function getCanonicalVisual(product) {
  if (!product) return null;

  // Canonical: visual first
  if (typeof product.visual === "string" && product.visual.trim())
    return product.visual.trim();

  // Back-compat
  if (typeof product.colorCode === "string" && product.colorCode.trim())
    return product.colorCode.trim();

  // Legacy/inherit support: only use `color` if it's a non-null string
  if (typeof product.color === "string" && product.color.trim())
    return product.color.trim();

  return null;
}

function renderStrokeColor(line, { isPreview = false } = {}) {
  const isFree =
    !!line?.isFreeMark || String(line?.kind || "").startsWith("free-");

  // Free marks MUST use their own stored color (never inherit product color).
  const prodColor =
    !isFree && line?.currentProduct
      ? getCanonicalVisual(line.currentProduct)
      : null;

  const base = isFree
    ? line?.color || "#000"
    : prodColor || line?.color || "#000";

  if (isPreview && (line?.isHorizontal || line?.isVertical)) {
    // alignment hint only while drawing
    return "#00c853";
  }
  return base;
}

function feetFromPx(px, gridSize, feetPerSquare) {
  return Number(px || 0) * (Number(feetPerSquare || 1) / Number(gridSize || 1));
}
function roundToQuarterFeet(n) {
  return Math.round(Number(n || 0) * 4) / 4;
}
function distancePxOf(line) {
  return calculateDistance([line.startX, line.startY], [line.endX, line.endY]);
}

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const titleCase = (s = "") => s.replace(/\b\w/g, (c) => c.toUpperCase());

function normalizeProductForLine(product) {
  if (!product) return null;

  const raw = product.raw || product;

  const color =
    raw?.drawingDefaults?.color ||
    product?.color ||
    raw?.visual ||
    raw?.colorCode ||
    raw?.color ||
    raw?.defaultColor ||
    "#000000";

  const price = Number(
    raw?.pricing?.unitPrice ??
      product?.pricing?.unitPrice ??
      raw?.price ??
      product?.price ??
      0,
  );

  return {
    ...raw,
    _id: raw?._id || raw?.id || product?.id,
    id: raw?._id || raw?.id || product?.id,
    name: raw?.name || product?.label || product?.value || "Catalog Item",
    price,
    color,
    colorCode: raw?.colorCode || color,
    visual: raw?.visual || color,
    drawingDefaults: {
      ...(raw?.drawingDefaults || {}),
      color,
    },
    pricing: {
      ...(raw?.pricing || {}),
      unitPrice: price,
    },
    uiBehavior: raw?.uiBehavior || product?.uiBehavior || "draw",
    toolGroup: raw?.toolGroup || product?.toolGroup || "general",
    assembly: raw?.assembly ||
      product?.assembly || { enabled: false, type: "none" },
    measurementFamily:
      raw?.measurementFamily || product?.measurementFamily || "line",
    drawingToolFamily:
      raw?.drawingToolFamily || product?.drawingToolFamily || "line",
  };
}

function productFromToolName(toolValue, allProducts, activeTool = null) {
  if (activeTool?.raw || activeTool?.id) {
    return normalizeProductForLine(activeTool);
  }

  if (!toolValue || !Array.isArray(allProducts)) return null;

  const p = allProducts.find((x) => String(x?.name) === String(toolValue));
  return normalizeProductForLine(p);
}

function applyCatalogToolMetadata(
  lineDraft,
  activeTool,
  toolValue,
  allProducts,
) {
  const prod = productFromToolName(toolValue, allProducts, activeTool);
  if (!prod) return lineDraft;

  const isGutter =
    /gutter/i.test(String(prod.name || "")) ||
    String(prod.category || "").toLowerCase() === "gutters" ||
    String(prod.toolGroup || "").toLowerCase() === "gutters" ||
    String(prod.assembly?.type || "")
      .toLowerCase()
      .includes("gutter");

  return {
    ...lineDraft,
    currentProduct: prod,
    productId: prod._id || prod.id || null,
    color:
      prod.drawingDefaults?.color || prod.visual || prod.color || "#000000",

    isGutter,

    catalogItemId: prod._id || prod.id || null,
    catalogSource: "businessCatalog",
    uiBehavior: prod.uiBehavior || activeTool?.uiBehavior || "draw",
    toolGroup: prod.toolGroup || activeTool?.toolGroup || "general",
    assembly: prod.assembly ||
      activeTool?.assembly || { enabled: false, type: "none" },

    measurementFamily:
      prod.measurementFamily || activeTool?.measurementFamily || "line",
    drawingToolFamily:
      prod.drawingToolFamily || activeTool?.drawingToolFamily || "line",

    profileKey: prod.profile || lineDraft.profileKey || "",
    sizeInches: prod.size || lineDraft.sizeInches || "",
  };
}

// Parse labels like `6" K-Style`, `5" Straight Face`, `6" Half Round`, `6" Box`
function splitSizeAndProfileFromKey(label) {
  const s = String(label || "").trim();
  // size at the start: 5", 6", 7"
  const m = s.match(/^\s*(\d+)\s*"\s*(.+)$/i);
  let sizeInches = null;
  let profileKey = "custom";

  if (m) {
    sizeInches = `${m[1]}"`;
    const rest = m[2].toLowerCase();
    if (rest.includes("k")) profileKey = "k-style";
    else if (rest.includes("straight")) profileKey = "straight-face";
    else if (rest.includes("half")) profileKey = "half-round";
    else if (rest.includes("box")) profileKey = "box";
    else if (rest.includes("custom")) profileKey = "custom";
  } else {
    // no leading size — try to infer profile only
    const low = s.toLowerCase();
    if (low.includes("k")) profileKey = "k-style";
    else if (low.includes("straight")) profileKey = "straight-face";
    else if (low.includes("half")) profileKey = "half-round";
    else if (low.includes("box")) profileKey = "box";
    else if (low.includes("custom")) profileKey = "custom";
  }

  return { sizeInches, profileKey };
}

const PROFILE_ALIASES = [
  [/k[-\s]?style/i, "K-Style"],
  [/half[-\s]?round/i, "Half Round"],
  [/straight[-\s]?face|straightface/i, "Straight Face"],
  [/box/i, "Box"],
  [/round(?!.*half)/i, "Round"],
  [/custom/i, "Custom"],
];

function formatDownspoutName(sizeRaw, profileRaw) {
  const s = String(sizeRaw || "").trim();
  const p = String(profileRaw || "").trim();
  const corr = /corr/i.test(p);
  const round = /round/i.test(p);
  // normalize to: 3x4 Corrugated Downspout  OR  3" Round Downspout
  if (/^\d+\s*x\s*\d+$/i.test(s)) {
    return `${s.replace(/\s*/g, "")} ${
      corr
        ? "Corrugated"
        : /smooth/i.test(p)
          ? "Smooth"
          : /box/i.test(p)
            ? "Box"
            : p
    } Downspout`;
  }
  const m = s.match(/^(\d+)/);
  if (m && round) return `${m[1]}" Round Downspout`;
  // fallback
  return `${s} ${p} Downspout`.replace(/\s+/g, " ").trim();
}

// ---- Catalog matching helpers (robust to your seedTemplate) ----

// normalize
const ci = (s) => String(s || "").toLowerCase();

// Map gutter drawing profile -> tokens we can match in template *names*
function nameTokensForProfile(profileKey) {
  const p = ci(profileKey);
  if (p.includes("k")) return ["k-style"];
  if (p.includes("straight")) return ["straight face", "straight-face"];
  if (p.includes("half")) return ["half round", "half-round"];
  if (p.includes("box")) return ["box"];
  if (p.includes("custom")) return ["custom"];
  // fallback
  return [p];
}

// “size” for gutters is like 5", 6", 7"
function inchesToken(sizeInches) {
  const s = String(sizeInches || "").replace(/\s+/g, "");
  return /(\d+)"/.test(s) ? s : `${s.replace(/[^0-9]/g, "")}"`;
}

// For downspouts: 2x3 / 3x4 / 4x5 / 3" / 4"
function dsSizeToken(dsSizeRaw) {
  const s = ci(dsSizeRaw).replace(/\s+/g, "");
  if (/^\d+x\d+$/.test(s)) return s; // 3x4
  const m = s.match(/^(\d+)("?|inch|in)$/); // 3"
  return m ? `${m[1]}"` : s;
}

// does a product *name* contain one of tokens?
function nameHasAny(n, tokens = []) {
  const S = ci(n);
  return tokens.some((t) => S.includes(ci(t)));
}

// Find an accessory template by *name* patterns more than by profile field.
// kind: "end cap" | "strip miter" | "bay miter"
function findGutterAccessoryTemplate(
  allProducts,
  { profileKey, sizeInches, kind },
) {
  const sizeTok = inchesToken(sizeInches); // e.g., 5"
  const profNameTokens = nameTokensForProfile(profileKey); // e.g., ["k-style"] or ["half round", "half-round"]

  const kindTokens = ci(kind); // "end cap" / "strip miter" / "bay miter"
  const isEndCap = kindTokens.includes("end");
  const isStrip = kindTokens.includes("strip");
  const isBay = kindTokens.includes("bay");

  // pass 1: strict -> name must include profile token + size + kind words
  let hit = allProducts.find((p) => {
    const n = p.name || "";
    if (!/accessory/i.test(p.type || "accessory")) return false;
    if (!nameHasAny(n, profNameTokens)) return false;
    if (!n.includes(sizeTok)) return false;
    if (isEndCap && !/end\s*cap|endcap/i.test(n)) return false;
    if (isStrip && !/strip\s*miter/i.test(n)) return false;
    if (isBay && !/bay\s*miter/i.test(n)) return false;
    return true;
  });
  if (hit) return hit;

  // pass 2: relaxed -> ignore size (some entries are size-less for custom/box)
  hit = allProducts.find((p) => {
    const n = p.name || "";
    if (!/accessory/i.test(p.type || "accessory")) return false;
    if (!nameHasAny(n, profNameTokens)) return false;
    if (isEndCap && !/end\s*cap|endcap/i.test(n)) return false;
    if (isStrip && !/strip\s*miter/i.test(n)) return false;
    if (isBay && !/bay\s*miter/i.test(n)) return false;
    return true;
  });
  return hit || null;
}

// Downspout elbows/offsets: match by profile family + size + code A/B (when present)
// Requires helpers already in your file:
//   - ci: (s) => String(s || "").toLowerCase()
//   - nameHasAny: (name, tokens[]) => tokens.some(t => String(name).toLowerCase().includes(String(t).toLowerCase()))
//   - dsSizeToken: (dsSize) => '2x3' | '3x4' | '3"' | '4"' (normalized)
function findDownspoutFitting(allProducts, { profileKey, dsSize, code, kind }) {
  const catalog = Array.isArray(allProducts) ? allProducts : [];
  const K = ci(kind || ""); // e.g., "elbow", '2" offset'
  const prof = ci(profileKey || ""); // corrugated | smooth | box | round
  const sizeTok = dsSizeToken(dsSize || ""); // "3x4", "2x3", '3"', '4"'

  // Profile tokens used in product NAME matching
  let profTokens = [];
  if (prof.includes("corr")) profTokens = ["corrugated"];
  else if (prof.includes("smooth")) profTokens = ["smooth"];
  else if (prof.includes("box")) profTokens = ["box"];
  else if (prof.includes("round")) profTokens = ["round"];

  const wantElbow = /elbow/i.test(K);
  const wantOffset = /offset/i.test(K);

  // ---- elbow code handling (A/B) ----
  // Non-round elbow SKUs have explicit A/B; round elbows don't.
  const needCode = wantElbow && !prof.includes("round") && !!code;
  const codeSafe = code
    ? String(code).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    : "";
  const codeRe = needCode ? new RegExp(`\\b${codeSafe}\\b`, "i") : null;

  // ---- size matching helpers ----
  const sizeNeedle = String(sizeTok || "").toLowerCase(); // '2x3' | '3"' etc.
  function nameHasSize(n) {
    const s = String(n || "").toLowerCase();
    if (!sizeNeedle) return true;
    // '2x3', '2 x 3', '2X3'
    if (/^\d+x\d+$/.test(sizeNeedle)) {
      const [w, h] = sizeNeedle.split("x");
      const re = new RegExp(`\\b${w}\\s*[xX]\\s*${h}\\b`);
      return re.test(s);
    }
    // round inches, e.g. 3" or 4"
    if (/^\d+"\s*$/.test(sizeNeedle)) {
      const w = sizeNeedle.replace(/"/g, "");
      return s.includes(`${w}"`) || new RegExp(`\\b${w}\\b`).test(s);
    }
    return s.includes(sizeNeedle);
  }

  // ---- offset inches (parsed from kind) ----
  // Supports '2" offset', '4 in offset', '6 inch offset'
  let inchesRe = null;
  if (wantOffset) {
    const m = K.match(/(\d+)\s*(?:"|in\b|inch\b|”)?/i);
    if (m) {
      const inchesSafe = m[1].replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      // Match 2" or bare 2
      inchesRe = new RegExp(
        `(?:\\b${inchesSafe}"\\b|\\b${inchesSafe}\\b)`,
        "i",
      );
    }
  }

  // ---- base type checks ----
  function isDesiredKind(n) {
    const s = String(n || "");
    if (wantElbow && !/elbow/i.test(s)) return false;
    if (wantOffset && !/offset/i.test(s)) return false;
    return wantElbow || wantOffset; // must be one of them
  }

  // ---------- PASS 1: strict (profile + size + kind + code if needed + inches for offset) ----------
  let hit = catalog.find((p) => {
    const n = p?.name || "";
    if (!/accessory/i.test(p?.type || "accessory")) return false;
    if (!nameHasAny(n, profTokens)) return false;
    if (!isDesiredKind(n)) return false;
    if (!nameHasSize(n)) return false;
    if (needCode && !(codeRe && codeRe.test(n))) return false;
    if (wantOffset && inchesRe && !inchesRe.test(n)) return false;
    return true;
  });
  if (hit) return hit;

  // ---------- PASS 2: allow size-less catalog entries (e.g., "Box A Elbow") ----------
  hit = catalog.find((p) => {
    const n = p?.name || "";
    if (!/accessory/i.test(p?.type || "accessory")) return false;
    if (!nameHasAny(n, profTokens)) return false;
    if (!isDesiredKind(n)) return false;
    if (needCode && !(codeRe && codeRe.test(n))) return false;
    if (wantOffset && inchesRe && !inchesRe.test(n)) return false;
    return true;
  });
  if (hit) return hit;

  // ---------- PASS 3: round elbows with no A/B (ignore code entirely) ----------
  if (wantElbow && prof.includes("round")) {
    hit = catalog.find((p) => {
      const n = p?.name || "";
      if (!/accessory/i.test(p?.type || "accessory")) return false;
      if (!nameHasAny(n, ["round"])) return false;
      if (!/elbow/i.test(n)) return false;
      if (sizeNeedle && !nameHasSize(n)) return false;
      return true;
    });
    if (hit) return hit;
  }

  // ---------- PASS 4: last resort (profile + kind only) ----------
  hit = catalog.find((p) => {
    const n = p?.name || "";
    if (!/accessory/i.test(p?.type || "accessory")) return false;
    if (!nameHasAny(n, profTokens)) return false;
    if (!isDesiredKind(n)) return false;
    return true;
  });

  return hit || null;
}

function normalizeGutterKey(name = "") {
  const trimmed = name.trim();
  const sizeMatch = trimmed.match(/(\d+)\s*"/);
  if (!sizeMatch) return trimmed;

  const size = `${sizeMatch[1]}"`;
  for (const [rx, label] of PROFILE_ALIASES) {
    if (rx.test(trimmed)) return `${size} ${label}`;
  }

  // fallback: grab a couple of non-junk words after size
  const after = trimmed.slice(sizeMatch.index + sizeMatch[0].length);
  const stop = /[-–—,(\/\\|]/;
  const cut = after.search(stop);
  let chunk = (cut >= 0 ? after.slice(0, cut) : after)
    .replace(
      /\b(alum(?:inum)?|copper|steel|gutter|seamless|paint(?:ed)?|color|white|black|bronze|brown|matte|textured|coil|sku|ft|pcs?)\b/gi,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();

  chunk = chunk.split(/\s+/).filter(Boolean).slice(0, 2).join(" ");

  return chunk ? `${size} ${titleCase(chunk)}` : size;
}

// Async replacement for canvas.toDataURL() to avoid blocking the main thread
function canvasToDataURLAsync(canvas, type = "image/png", quality) {
  const DEV = true;
  return new Promise((resolve, reject) => {
    if (DEV) {
      try {
        performance.mark("PDF/Canvas snapshot:start");
      } catch {}
    }
    try {
      if (canvas.toBlob) {
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve("");
            const reader = new FileReader();
            reader.onloadend = () => {
              if (DEV) {
                try {
                  performance.mark("PDF/Canvas snapshot:end");
                  performance.measure(
                    "PDF/Canvas snapshot",
                    "PDF/Canvas snapshot:start",
                    "PDF/Canvas snapshot:end",
                  );
                } catch {}
              }
              resolve(String(reader.result || ""));
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          type,
          quality,
        );
      } else {
        // Fallback (older Safari), still synchronous but rarely hit
        const out = canvas.toDataURL(type, quality);
        if (DEV) {
          try {
            performance.mark("PDF/Canvas snapshot:end");
            performance.measure(
              "PDF/Canvas snapshot",
              "PDF/Canvas snapshot:start",
              "PDF/Canvas snapshot:end",
            );
          } catch {}
        }
        resolve(out);
      }
    } catch (e) {
      if (DEV) {
        try {
          performance.mark("PDF/Canvas snapshot:end");
          performance.measure(
            "PDF/Canvas snapshot (error)",
            "PDF/Canvas snapshot:start",
            "PDF/Canvas snapshot:end",
          );
        } catch {}
      }
      resolve("");
    }
  });
}

// ======= Component =======
const Diagram = ({
  activeModal,
  closeModal,
  isMobile,
  currentProjectId,
  updateDiagram,
  addDiagramToProject,
  handlePassDiagramData,
  selectedDiagram,
  setSelectedDiagram,
  originalDiagram,
  diagrams,
  setActiveModal,
}) => {
  // ------- Constants for UX fidelity on phones -------
  const MIN_HANDLE_PX = 6; // CSS pixels minimum for handles
  const PADDING_PX = 70; // auto-fit padding
  // Products (context first, then API)
  // ✅ Call hooks once at top-level, never conditionally
  const { data: pricingProducts = [], isLoading, error } = useProductsPricing(); // ALL items
  // which downspout we are editing via the modal (null = adding a new one)
  const [editingDownspoutIndex, setEditingDownspoutIndex] = useState(null);
  const { track } = useOnboarding();

  const { data: listedProducts = [] } = useProductsListed(); // fallback (optional)

  const productsCtx = useProductsCatalog();
  // which annotation (note) is being edited; null means "creating"
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  const lastHydratedDiagramIdRef = useRef(null); // track last hydrated diagram ID to avoid redundant hydration

  const displayTransformRef = useRef({
    diagramId: null,
    sx: 1,
    sy: 1,
    kAvg: 1,
    baseMeta: null,
  });
  // click-vs-drag detection for notes (like you already do for downspout box)
  const noteClickRef = useRef({ x: 0, y: 0, index: null });
  // Scroll-lock state (must persist across renders)
  const scrollLockRef = useRef({
    locked: false,
    prevBodyOverflow: "",
    prevHtmlOverflow: "",
  });

  const allProducts =
    pricingProducts && pricingProducts.length
      ? pricingProducts
      : listedProducts;

  const boxClickRef = useRef({ x: 0, y: 0, index: null });
  const dragOriginRef = useRef(null);

  // Cycle-through selection when clicking stacked elements
  const selectCycleRef = useRef({
    x: 0,
    y: 0,
    stack: [], // indices (top-first)
    idx: -1, // current position in stack
    time: 0, // last click ts
  });

  function getHitStackAt(x, y) {
    // Build top-first list of indices that are hit at (x,y)
    const hits = [];
    for (let i = lines.length - 1; i >= 0; i--) {
      const el = lines[i];
      let ok = false;

      if (el.isNote) {
        ok = !!hitTestAnnotation(el, x, y);
      } else if (el.isDownspout) {
        ok = !!hitTestDownspout(el, x, y);
      } else if (el.isFreeMark) {
        if (el.kind === "free-line") {
          ok = !!hitTestLine(
            {
              startX: el.startX,
              startY: el.startY,
              endX: el.endX,
              endY: el.endY,
            },
            x,
            y,
          );
        } else if (el.kind === "free-square") {
          ok = hitTestFreeSquare(el, x, y);
        } else if (el.kind === "free-circle") {
          ok = hitTestFreeCircle(el, x, y);
        }
      } else {
        // gutter/priced line
        ok = !!hitTestLine(el, x, y);
      }

      if (ok) hits.push(i);
    }
    return hits; // top-most first
  }

  // ---- FIX: always restore scrolling after closing the diagram on mobile ----
  useEffect(() => {
    const visible = [
      "diagram",
      "downspout",
      "selectedLine",
      "confirmDiagramOverwrite",
      "note",
    ].includes(activeModal);

    // Lock (only once per open)
    if (visible && !scrollLockRef.current.locked) {
      scrollLockRef.current.locked = true;

      // capture whatever inline values existed BEFORE we overwrite them
      scrollLockRef.current.prevBodyOverflow =
        document.body.style.overflow || "";
      scrollLockRef.current.prevHtmlOverflow =
        document.documentElement.style.overflow || "";

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }

    // Unlock (only if we previously locked)
    if (!visible && scrollLockRef.current.locked) {
      document.body.style.overflow = scrollLockRef.current.prevBodyOverflow;
      document.documentElement.style.overflow =
        scrollLockRef.current.prevHtmlOverflow;

      scrollLockRef.current.locked = false;
      scrollLockRef.current.prevBodyOverflow = "";
      scrollLockRef.current.prevHtmlOverflow = "";
    }

    // Cleanup: if component unmounts while locked, unlock no matter what
    return () => {
      if (scrollLockRef.current.locked) {
        document.body.style.overflow = scrollLockRef.current.prevBodyOverflow;
        document.documentElement.style.overflow =
          scrollLockRef.current.prevHtmlOverflow;

        scrollLockRef.current.locked = false;
        scrollLockRef.current.prevBodyOverflow = "";
        scrollLockRef.current.prevHtmlOverflow = "";
      }
    };
  }, [activeModal]);

  useEffect(() => {
    if (activeModal === "diagram") {
      setCanvasSize();
      drawScene();
    }
  }, [activeModal]);

  // When opening the modal to create a NEW diagram, make sure canvas is blank,
  // but only once per open and only if there is no content yet.
  useEffect(() => {
    if (activeModal !== "diagram") return;

    if (didInitDiagram.current) return; // already initialized this open

    const hasId =
      selectedDiagram &&
      typeof selectedDiagram === "object" &&
      selectedDiagram._id;

    // only clear if it's truly a brand-new diagram AND you don't already have lines
    if (!hasId && (lines?.length ?? 0) === 0) {
      setLines([]);
      setSelectedIndex(null);
      setIsDrawing(false);
      if (baselineHashRef && "current" in baselineHashRef) {
        baselineHashRef.current = hashLines([]);
      }
      drawScene();
    }

    didInitDiagram.current = true; // mark initialized for this open
  }, [activeModal]); // keep dep list tight

  useEffect(() => {
    if (activeModal !== "diagram") return;

    const el = canvasRef.current?.parentElement;
    if (!el) return;

    const resizeAndDraw = () => {
      setCanvasSize();
      requestAnimationFrame(drawScene);
    };

    const ro = new ResizeObserver(resizeAndDraw);
    ro.observe(el);

    window.addEventListener("resize", resizeAndDraw);
    window.visualViewport?.addEventListener("resize", resizeAndDraw);

    resizeAndDraw();

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", resizeAndDraw);
      window.visualViewport?.removeEventListener("resize", resizeAndDraw);
    };
  }, [activeModal]);

  // Keep a stable ref to the reload function for event listeners
  const reloadRef = useRef(null);
  useEffect(() => {
    reloadRef.current = productsCtx?.reload || productsCtx?.refetch || null;
  }, [productsCtx]);

  // Only gutter products in the dropdown
  const filteredProducts = useMemo(() => {
    const list = Array.isArray(allProducts) ? allProducts : [];
    return list.filter((p) => {
      const type = (p?.type || p?.category || "").toLowerCase();
      const name = (p?.name || "").toLowerCase();
      const isGutterByType = type === "gutter";
      const isGutterByName = name.includes("gutter"); // safety net
      const isListed = p?.listed !== false; // default to visible unless explicitly false
      return (isGutterByType || isGutterByName) && isListed;
    });
  }, [allProducts]);

  const catalogTools = useMemo(() => {
    const sourceProducts =
      listedProducts && listedProducts.length
        ? listedProducts
        : filteredProducts;

    const seen = new Set();

    return (sourceProducts || [])
      .filter((p) => {
        const name = String(p?.name || "").toLowerCase();
        const type = String(p?.type || p?.category || "").toLowerCase();

        return (
          p?.listed !== false && (type === "gutter" || name.includes("gutter"))
        );
      })
      .filter((p) => {
        const key = String(p?.name || "")
          .trim()
          .toLowerCase();

        if (!key) return false;
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
      })
      .map((p) => catalogItemToTool(p));
  }, [listedProducts, filteredProducts]);

  const drawTools = useMemo(() => {
    return catalogTools.filter((t) => t.uiBehavior === "draw");
  }, [catalogTools]);

  const assemblyTools = useMemo(() => {
    return catalogTools.filter((t) => t.uiBehavior === "assembly");
  }, [catalogTools]);

  const queryClient = useQueryClient();
  const [, force] = useState(0);
  useEffect(() => {
    const bump = async () => {
      // 1) Tell context to refetch if it exposes a loader
      if (typeof reloadRef.current === "function") {
        try {
          await reloadRef.current();
        } catch (e) {
          /* no-op */
        }
      }
      // 2) Invalidate React Query caches used by Diagram/useProducts()
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["gutterProducts"] });
      queryClient.invalidateQueries({ queryKey: ["downspoutProducts"] });
      // 3) Ensure a re-render in case something is memoized upstream
      force((v) => v + 1);
    };

    const onStorage = (e) => {
      if (e.key === "catalogVersion") bump();
    };

    window.addEventListener("catalog:updated", bump);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("catalog:updated", bump);
      window.removeEventListener("storage", onStorage);
    };
  }, [queryClient]);
  useEffect(() => {}, []);
  // canvas + state
  const canvasRef = useRef(null);

  const [activeTool, setActiveTool] = useState(null);

  const [tool, setTool] = useState(""); // dropdown selection
  const [isDrawing, setIsDrawing] = useState(false);
  const [gridSize, setGridSize] = useState(10);
  const [feetPerSquare, setFeetPerSquare] = useState(1);

  useEffect(() => {
    if (activeModal === "diagram") {
      drawScene();
    }
  }, [gridSize, feetPerSquare, activeModal]);

  const [currentLine, setCurrentLine] = useState({
    id: null,
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    isHorizontal: false,
    isVertical: false,
    isSelected: false,
    color: "black",
  });

  const [lines, setLines] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [dragging, setDragging] = useState({
    mode: "none", // "none" | "move" | "drag-end"
    end: null, // "start" | "end"
    lastX: 0,
    lastY: 0,
  });

  function setCanvasSize() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement || canvas;
    const dpr = window.devicePixelRatio || 1;

    const cssW = Math.max(1, parent.clientWidth);
    const cssH = Math.max(1, parent.clientHeight);

    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    canvas.style.width = cssW + "px";
    canvas.style.height = cssH + "px";

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // draw in CSS pixels
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // ------------ Utility: numeric safety & clamping ------------
  const isFiniteNumber = (n) => Number.isFinite(n) && !Number.isNaN(n);
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  function clampLine(l, cw, ch) {
    const copy = { ...l };
    const clampX = (x) => clamp(isFiniteNumber(x) ? x : 0, 0, cw);
    const clampY = (y) => clamp(isFiniteNumber(y) ? y : 0, 0, ch);
    if (copy.startX != null) copy.startX = clampX(copy.startX);
    if (copy.startY != null) copy.startY = clampY(copy.startY);
    if (copy.endX != null) copy.endX = clampX(copy.endX);
    if (copy.endY != null) copy.endY = clampY(copy.endY);
    if (copy.centerX != null) copy.centerX = clampX(copy.centerX);
    if (copy.centerY != null) copy.centerY = clampY(copy.centerY);
    if (copy.radius != null && !isFiniteNumber(copy.radius)) copy.radius = 0;
    return copy;
  }

  function contentBBox(arr = []) {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const l of arr) {
      if (
        l.isFreeMark &&
        l.kind === "free-circle" &&
        isFiniteNumber(l.centerX) &&
        isFiniteNumber(l.centerY)
      ) {
        const r = Number(l.radius || 0);
        minX = Math.min(minX, l.centerX - r);
        minY = Math.min(minY, l.centerY - r);
        maxX = Math.max(maxX, l.centerX + r);
        maxY = Math.max(maxY, l.centerY + r);
        continue;
      }
      const xs = [l.startX, l.endX, l.centerX].filter(isFiniteNumber);
      const ys = [l.startY, l.endY, l.centerY].filter(isFiniteNumber);
      if (xs.length) {
        minX = Math.min(minX, ...xs);
        maxX = Math.max(maxX, ...xs);
      }
      if (ys.length) {
        minY = Math.min(minY, ...ys);
        maxY = Math.max(maxY, ...ys);
      }
    }
    if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
      return {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        width: 0,
        height: 0,
        empty: true,
      };
    }
    return {
      left: minX,
      top: minY,
      right: maxX,
      bottom: maxY,
      width: Math.max(0, maxX - minX),
      height: Math.max(0, maxY - minY),
      empty: false,
    };
  }

  function getCurrentMeta() {
    const c = canvasRef.current;
    const rect = c?.getBoundingClientRect?.() || { width: 0, height: 0 };
    return {
      canvasW: Math.round(rect.width || 0),
      canvasH: Math.round(rect.height || 0),
      dpr: window.devicePixelRatio || 1,
      gridSize,
      feetPerSquare,
      version: 1,
    };
  }

  function sanitizeLines(arr, cw, ch) {
    return (arr || []).map((l) => clampLine(l, cw, ch)).filter(Boolean);
  }

  function buildDiagramPayload() {
    const meta = getCurrentMeta();
    const safe = sanitizeLines(lines, meta.canvasW, meta.canvasH);
    return { lines: safe, meta };
  }

  // one true render path
  function drawScene() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // grid first
    drawGrid(ctx);

    (lines || []).forEach((L) => drawLine(ctx, L, { isPreview: false }));

    // in-progress line (preview = true -> may render green)
    if (isDrawing && currentLine)
      drawLine(ctx, currentLine, { isPreview: true });
  }

  const [downspoutCoordinates, setDownspoutCoordinates] = useState([0, 0]);
  const [noteCoordinates, setNoteCoordinates] = useState([0, 0]);
  const [lineLength, setLineLength] = useState(0);
  const [isDownspoutModalOpen, setIsDownspoutModalOpen] = useState(false);

  // listen for catalog bumps and ask the context to reload when possible
  // listen for catalog bumps and ask the context to reload when possible
  useEffect(() => {
    const onBump = () => {
      try {
        const reload = reloadRef.current;
        if (typeof reload === "function") reload();
      } catch (e) {
        console.warn(
          "Diagram: catalog reload failed (will still re-render):",
          e,
        );
      }
      // force a repaint at minimum
      setLines((prev) => [...prev]);
    };

    const onStorage = (e) => {
      if (e.key === "catalogVersion") onBump();
    };

    window.addEventListener("catalog:updated", onBump);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("catalog:updated", onBump);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (activeModal === "diagram") {
      // ctx may already be set; safe to call
      drawScene();
    }
  }, [activeModal, lines]); // lines array is your source of truth

  // default tool -> first gutter
  useEffect(() => {
    if (!tool && filteredProducts.length > 0) {
      setTool(filteredProducts[0].name);
    }
  }, [filteredProducts, tool]);

  useEffect(() => {
    const diagramVisible = [
      "diagram",
      "downspout",
      "selectedLine",
      "confirmDiagramOverwrite",
      "note",
    ].includes(activeModal);

    if (diagramVisible) return;

    didInitDiagram.current = false;
    lastHydratedDiagramIdRef.current = null;

    displayTransformRef.current = {
      diagramId: null,
      sx: 1,
      sy: 1,
      kAvg: 1,
      baseMeta: null,
    };

    setSelectedIndex(null);
    setIsDrawing(false);
    setDragging({ mode: "none", end: null, lastX: 0, lastY: 0 });
  }, [activeModal]);

  // hydrate lines when a diagram is selected
  useEffect(() => {
    if (activeModal !== "diagram") return;

    const diagramId = selectedDiagram?._id || selectedDiagram?.id || null;

    const hasLines = Array.isArray(selectedDiagram?.lines);

    if (!diagramId || !hasLines) return;
    if (lastHydratedDiagramIdRef.current === diagramId) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (!ctx) return;

    setCanvasSize();
    const rectNow = canvas.getBoundingClientRect();
    if (rectNow.width < 20 || rectNow.height < 20) {
      requestAnimationFrame(() => {
        setCanvasSize();
        drawScene();
      });
    }

    const rect = rectNow;
    const curW = Math.max(1, Math.round(rect.width));
    const curH = Math.max(1, Math.round(rect.height));

    // ----- Upgrader/backfill for old diagrams without meta -----
    const metaIn = selectedDiagram?.meta || {};
    const savedW = Number(metaIn.canvasW) || curW;
    const savedH = Number(metaIn.canvasH) || curH;
    const alreadyFittedToThisCanvas =
      metaIn.fitCanvasW === curW && metaIn.fitCanvasH === curH;
    const savedGrid =
      Math.round(Number(metaIn.gridSize)) || Math.round(gridSize);
    const savedFeet =
      Math.round(Number(metaIn.feetPerSquare)) || Math.round(feetPerSquare);

    // Do not set gridSize to savedGrid here.
    // We scale coordinates for display below, so the display grid must be scaled too.
    // feetPerSquare can stay the saved value.
    if (metaIn && (metaIn.gridSize || metaIn.feetPerSquare)) {
      if (savedFeet !== feetPerSquare) {
        setFeetPerSquare(savedFeet);
      }
    }

    // If this diagram was saved on a different canvas size, scale coordinates
    const sx = curW / savedW;
    const sy = curH / savedH;
    const kAvg = (sx + sy) / 2; // keep px/ft stable across anisotropic scale
    displayTransformRef.current = {
      diagramId,
      sx,
      sy,
      kAvg,
      baseMeta: {
        ...(metaIn || {}),
        canvasW: savedW,
        canvasH: savedH,
        gridSize: savedGrid,
        feetPerSquare: savedFeet,
        dpr: Number(metaIn.dpr) || window.devicePixelRatio || 1,
        version: Number(metaIn.version) || 1,
      },
    };

    console.log("[hydrate-scale]", {
      diagramId,
      savedW,
      savedH,
      curW,
      curH,
      sx,
      sy,
      kAvg,
      savedGrid,
      gridSize,
      desiredGrid: savedGrid * kAvg,
      savedFeet,
    });

    const scaleLine = (l) => {
      const copy = { ...l };

      // Priced gutter/downspout lines
      if (!copy.isFreeMark && !copy.isNote) {
        if (copy.startX != null) copy.startX = copy.startX * sx;
        if (copy.startY != null) copy.startY = copy.startY * sy;
        if (copy.endX != null) copy.endX = copy.endX * sx;
        if (copy.endY != null) copy.endY = copy.endY * sy;
      }

      // Free-line
      if (copy.isFreeMark && copy.kind === "free-line") {
        if (copy.startX != null) copy.startX = copy.startX * sx;
        if (copy.startY != null) copy.startY = copy.startY * sy;
        if (copy.endX != null) copy.endX = copy.endX * sx;
        if (copy.endY != null) copy.endY = copy.endY * sy;
      }

      // Free-square
      if (copy.isFreeMark && copy.kind === "free-square") {
        if (copy.startX != null) copy.startX = copy.startX * sx;
        if (copy.startY != null) copy.startY = copy.startY * sy;
        if (copy.endX != null) copy.endX = copy.endX * sx;
        if (copy.endY != null) copy.endY = copy.endY * sy;
      }

      // Free-circle
      if (copy.isFreeMark && copy.kind === "free-circle") {
        if (copy.centerX != null) copy.centerX = copy.centerX * sx;
        if (copy.centerY != null) copy.centerY = copy.centerY * sy;
        if (copy.radius != null) copy.radius = copy.radius * ((sx + sy) / 2);
      }

      // Notes
      if (copy.isNote) {
        if (copy.startX != null) copy.startX = copy.startX * sx;
        if (copy.startY != null) copy.startY = copy.startY * sy;
      }

      return copy;
    };

    let withIds = (selectedDiagram?.lines || []).map((l) => ({
      id: l.id || newId(),
      ...scaleLine(l),
    }));

    // If coordinates are scaled to fit this screen,
    // the visual grid must scale by the same average factor.
    // Otherwise measurements explode on bigger screens.
    if (metaIn && (metaIn.gridSize || metaIn.feetPerSquare)) {
      const desiredGrid = Math.max(1, savedGrid * kAvg);

      if (Math.abs(desiredGrid - gridSize) > 0.1) {
        setGridSize(desiredGrid);
      }

      if (savedFeet !== feetPerSquare) {
        setFeetPerSquare(savedFeet);
      }
    }

    // ----- Auto-fit only for legacy diagrams without saved scale meta -----
    // Important:
    // For diagrams with meta.canvasW/canvasH/gridSize, we already scale them
    // with sx/sy above. Applying another auto-fit transform makes save math unsafe
    // unless we also reverse that transform in unscaleLineForSave().
    const hasSavedScaleMeta = Boolean(
      metaIn?.canvasW &&
      metaIn?.canvasH &&
      metaIn?.gridSize &&
      metaIn?.feetPerSquare,
    );

    const bbox = contentBBox(withIds);

    if (!bbox.empty && !hasSavedScaleMeta) {
      const overLeft = bbox.left < -0.1 * curW;
      const overTop = bbox.top < -0.1 * curH;
      const overRight = bbox.right > 1.1 * curW;
      const overBottom = bbox.bottom > 1.1 * curH;
      const canvasChangedALot =
        Math.abs(curW - savedW) / savedW > 0.15 ||
        Math.abs(curH - savedH) / savedH > 0.15;

      const exceeds =
        !alreadyFittedToThisCanvas &&
        (overLeft || overTop || overRight || overBottom || canvasChangedALot);

      const bw = Math.max(1, bbox.width);
      const bh = Math.max(1, bbox.height);
      const k = Math.min(
        (curW - 2 * PADDING_PX) / bw,
        (curH - 2 * PADDING_PX) / bh,
      );
      if (exceeds && Number.isFinite(k) && k > 0 && k < 1_000) {
        // scale then center
        withIds = withIds.map((l) => {
          const copy = { ...l };
          const scale = (n) => Number(n) * k;
          if (copy.startX != null) copy.startX = scale(copy.startX - bbox.left);
          if (copy.endX != null) copy.endX = scale(copy.endX - bbox.left);
          if (copy.centerX != null)
            copy.centerX = scale(copy.centerX - bbox.left);
          if (copy.startY != null) copy.startY = scale(copy.startY - bbox.top);
          if (copy.endY != null) copy.endY = scale(copy.endY - bbox.top);
          if (copy.centerY != null)
            copy.centerY = scale(copy.centerY - bbox.top);
          if (copy.radius != null) copy.radius = Number(copy.radius) * k; // circles by uniform k
          // add offset to center inside canvas with padding
          const offX = PADDING_PX + (curW - 2 * PADDING_PX - bw * k) / 2;
          const offY = PADDING_PX + (curH - 2 * PADDING_PX - bh * k) / 2;
          if (copy.startX != null) copy.startX += offX;
          if (copy.endX != null) copy.endX += offX;
          if (copy.centerX != null) copy.centerX += offX;
          if (copy.startY != null) copy.startY += offY;
          if (copy.endY != null) copy.endY += offY;
          if (copy.centerY != null) copy.centerY += offY;
          return copy;
        });
        // VERY IMPORTANT: scale gridSize by the same k to preserve px-per-foot
        // setGridSize((g) => Math.max(1, g * k));
      }
    }

    // clamp to canvas and reject invalids
    // clamp to canvas and reject invalids
    withIds = sanitizeLines(withIds, curW, curH)
      // clear any cached derived props so we never render stale labels
      .map((l) => {
        const copy = { ...l, midpoint: undefined };
        if (copy.isDownspout) {
          const tf = Number(copy.totalFeet ?? copy.measurement ?? 0);
          copy.totalFeet = Number.isFinite(tf) ? tf : 0;
          copy.measurement = copy.totalFeet;
        } else if (copy.isGutter || copy.currentProduct) {
          const savedMeasurement = Number(copy.runFeet ?? copy.measurement);

          if (Number.isFinite(savedMeasurement) && savedMeasurement > 0) {
            copy.measurement = savedMeasurement;
            copy.runFeet = savedMeasurement;
          } else {
            const px = calculateDistance(
              [copy.startX, copy.startY],
              [copy.endX, copy.endY],
            );

            const displayGrid = Math.max(1, savedGrid * kAvg);
            const ft =
              Number(px || 0) *
              (Number(savedFeet || 1) / Number(displayGrid || 1));

            copy.measurement = Math.round(ft);
            copy.runFeet = copy.measurement;
          }
        } else {
          copy.measurement = undefined;
        }

        // --- GUTTER HYDRATION SAFETY ---
        if (copy.isGutter) {
          // Preserve snapshot fields if they exist on the saved line
          if (!copy.currentProduct && l.currentProduct)
            copy.currentProduct = l.currentProduct;
          if (!copy.profileKey && l.profileKey) copy.profileKey = l.profileKey;
          if (!copy.sizeInches && l.sizeInches) copy.sizeInches = l.sizeInches;
          if (!copy.color && l.color) copy.color = l.color;
        }

        return copy;
      });

    // draw the scaled content exactly as your code does now
    setLines(withIds);
    lastHydratedDiagramIdRef.current = diagramId;
    requestAnimationFrame(() => {
      setCanvasSize();
      drawScene();
    });

    // If you’re using the baseline-hash to decide overwrite prompts, reset it here
    if (
      typeof baselineHashRef !== "undefined" &&
      baselineHashRef?.current !== undefined
    ) {
      baselineHashRef.current = hashLines(withIds);
    }
  }, [activeModal, selectedDiagram?._id]);

  useEffect(() => {
    const diagramVisible = [
      "diagram",
      "downspout",
      "selectedLine",
      "confirmDiagramOverwrite",
      "note",
    ].includes(activeModal);

    if (!diagramVisible) return;

    requestAnimationFrame(() => {
      drawScene();
    });
  }, [activeModal, currentLine, lines, isDrawing]);

  // --- stable diagram hashing (only fields that define "meaningful change") ---
  const baselineHashRef = useRef(null);

  const didInitDiagram = useRef(false);

  function round2(n) {
    return Math.round(Number(n || 0) * 100) / 100;
  }

  function normColor(c) {
    return String(c || "")
      .trim()
      .toLowerCase();
  }

  useEffect(() => {
    if (activeModal !== "diagram") return;

    let raf = 0;

    function syncViewportAndCanvas() {
      const height = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty("--diagram-vh", `${height}px`);

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setCanvasSize();
        drawScene();
      });
    }

    syncViewportAndCanvas();

    window.visualViewport?.addEventListener("resize", syncViewportAndCanvas);
    window.addEventListener("resize", syncViewportAndCanvas);

    return () => {
      cancelAnimationFrame(raf);
      window.visualViewport?.removeEventListener(
        "resize",
        syncViewportAndCanvas,
      );
      window.removeEventListener("resize", syncViewportAndCanvas);
      document.documentElement.style.removeProperty("--diagram-vh");
    };
  }, [activeModal, selectedDiagram]);

  /**
   * Build a normalized snapshot of what matters to the diagram:
   * - Lines (coordinates rounded to 2 decimals, product id/name, kind, color, ds size)
   * - If you later want notes/free marks included, fold them in here similarly.
   */
  function hashLines(lines = []) {
    const norm = (l) => {
      // Notes
      if (l.isNote) {
        return {
          t: "note",
          x: round2(l.startX),
          y: round2(l.startY),
          text: String(l.note || "").trim(),
        };
      }

      // Downspouts (priced)
      if (l.isDownspout) {
        return {
          t: "ds",
          x: round2(l.startX),
          y: round2(l.startY),
          size: l.downspoutSize || null,
          // ✅ include profile in the hash so style changes are detected
          profile: String(l.profile || "")
            .trim()
            .toLowerCase(),
          // ✅ include footage (totalFeet/measurement) so footage edits are detected
          feet: Number(l.totalFeet ?? l.measurement ?? 0),
          // keep existing pieces
          seq: String(l.elbowSequence || "")
            .trim()
            .toUpperCase(),
          c: normColor(l.color || "#000"),
        };
      }

      // Free marks (non-priced)
      if (l.isFreeMark) {
        if (l.kind === "free-circle") {
          return {
            t: "fc",
            cx: round2(l.centerX),
            cy: round2(l.centerY),
            r: round2(l.radius || 0),
            fill: !!l.fill,
            c: normColor(l.color || "#111"),
            dashed: !!l.dashed,
            w: Number(l.strokeWidth || 2),
          };
        }
        if (l.kind === "free-square") {
          const left = Math.min(l.startX, l.endX);
          const top = Math.min(l.startY, l.endY);
          const w = Math.abs(l.endX - l.startX);
          const h = Math.abs(l.endY - l.startY);
          return {
            t: "fs",
            x: round2(left),
            y: round2(top),
            w: round2(w),
            h: round2(h),
            fill: !!l.fill,
            c: normColor(l.color || "#111"),
            dashed: !!l.dashed,
            sw: Number(l.strokeWidth || 2),
          };
        }
        // free-line
        return {
          t: "fl",
          x1: round2(l.startX),
          y1: round2(l.startY),
          x2: round2(l.endX),
          y2: round2(l.endY),
          c: normColor(l.color || "#111"),
          dashed: !!l.dashed,
          w: Number(l.strokeWidth || 2),
        };
      }

      // Gutters (priced)
      return {
        t: "gut",
        x1: round2(l.startX),
        y1: round2(l.startY),
        x2: round2(l.endX),
        y2: round2(l.endY),
        pid: l.currentProduct?._id || null,
        pname: l.currentProduct?.name || null,
        c: normColor(
          l.color ||
            l.currentProduct?.colorCode ||
            l.currentProduct?.color ||
            "#000",
        ),
      };
    };

    const core = (lines || []).map(norm);
    // order-independent
    core.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    return JSON.stringify(core);
  }

  // ======= Geometry / draw helpers =======
  function drawGrid(ctx) {
    const { width, height } = ctx.canvas;
    const size = gridSize;
    ctx.strokeStyle = "#ccc";
    ctx.lineWidth = 1;
    for (let x = 0; x < width; x += size) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += size) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }
  const convertToFeet = (dist) => {
    // dist is in pixels
    // each square is gridSize px and equals feetPerSquare feet
    // => pixels per foot = gridSize / feetPerSquare
    // => feet = pixels / (gridSize / feetPerSquare) = pixels * (feetPerSquare / gridSize)
    const pxPerFoot = gridSize / Math.max(0.0001, feetPerSquare);
    return Math.round(dist / pxPerFoot);
  };

  const snap = (n) => Math.round(n / gridSize) * gridSize;

  function getCanvasCoords(e) {
    const ne = e.nativeEvent;
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = ne?.clientX ?? ne?.touches?.[0]?.clientX ?? 0;
    const clientY = ne?.clientY ?? ne?.touches?.[0]?.clientY ?? 0;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function updateLineComputedProps(line) {
    line.midpoint = [
      (line.startX + line.endX) / 2,
      (line.startY + line.endY) / 2,
    ];
    line.measurement = convertToFeet(
      calculateDistance([line.startX, line.startY], [line.endX, line.endY]),
    );
    if (line.isGutter || line.currentProduct) {
      line.runFeet = line.measurement;
    }
    if (isLineParallelToSide(line.startX, line.startY, line.endX, line.endY)) {
      line.isVertical = true;
      line.isHorizontal = false;
      line.position =
        line.midpoint[0] >= window.innerWidth / 2 ? "right" : "left";
    } else if (
      isLineParallelToTop(line.startX, line.startY, line.endX, line.endY)
    ) {
      line.isHorizontal = true;
      line.isVertical = false;
      line.position =
        line.midpoint[1] <= window.innerHeight / 2 ? "top" : "bottom";
    } else {
      line.isVertical = false;
      line.isHorizontal = false;
    }
    return line;
  }

  // hit tests
  function hitTestLine(line, x, y) {
    const EP = Math.max(6, gridSize * 0.6);
    if (calculateDistance([x, y], [line.startX, line.startY]) <= EP)
      return { hit: "start" };
    if (calculateDistance([x, y], [line.endX, line.endY]) <= EP)
      return { hit: "end" };
    if (
      isLineNearPoint(line.startX, line.startY, line.endX, line.endY, x, y, 6)
    )
      return { hit: "body" };
    return null;
  }

  function hitTestFreeSquare(sq, x, y) {
    const left = Math.min(sq.startX, sq.endX);
    const top = Math.min(sq.startY, sq.endY);
    const w = Math.abs(sq.endX - sq.startX);
    const h = Math.abs(sq.endY - sq.startY);

    if (w <= 0 || h <= 0) return false;

    if (sq.fill) {
      // Click inside filled rect selects
      return x >= left && x <= left + w && y >= top && y <= top + h;
    }

    // For stroked (not filled): near any edge within tolerance
    const EP = Math.max(6, gridSize * 0.6);
    const onLeft = Math.abs(x - left) <= EP && y >= top && y <= top + h;
    const onRight = Math.abs(x - (left + w)) <= EP && y >= top && y <= top + h;
    const onTop = Math.abs(y - top) <= EP && x >= left && x <= left + w;
    const onBottom =
      Math.abs(y - (top + h)) <= EP && x >= left && x <= left + w;
    return onLeft || onRight || onTop || onBottom;
  }

  function hitTestFreeCircle(circ, x, y) {
    const cx = circ.centerX ?? circ.startX ?? 0;
    const cy = circ.centerY ?? circ.startY ?? 0;
    const R = Number(circ.radius || 0);
    if (R <= 0) return false;

    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.hypot(dx, dy);

    if (circ.fill) {
      // Click anywhere inside the filled circle
      return dist <= R;
    }

    // For stroked only: near the ring
    const EP = Math.max(6, gridSize * 0.6);
    return Math.abs(dist - R) <= EP;
  }

  function hitTestDownspout(ds, x, y) {
    const r = gridSize;
    // center/X
    if (calculateDistance([x, y], [ds.startX, ds.startY]) <= r) {
      return { part: "center" };
    }

    // compute the dynamic box rect exactly as in draw
    const w = 60;
    const h = 28;
    const angle =
      typeof ds.elbowBoxAngle === "number" ? ds.elbowBoxAngle : Math.PI / 4;
    const radius =
      typeof ds.elbowBoxRadius === "number" ? ds.elbowBoxRadius : gridSize * 5;

    const cx = ds.startX + radius * Math.cos(angle);
    const cy = ds.startY + radius * Math.sin(angle);
    const boxX = cx - w / 2;
    const boxY = cy - h / 2;

    if (x >= boxX && x <= boxX + w && y >= boxY && y <= boxY + h) {
      return { part: "box" };
    }

    return null;
  }

  function hitTestAnnotation(note, x, y) {
    const ctx = canvasRef.current.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.font = "1000 12px Arial";
    const text = note.note || "";
    const w = ctx.measureText(text).width;
    const padX = 6;
    const padY = 4;
    const h = 12;
    const x1 = note.startX - w / 2 - padX;
    const y1 = note.startY - h - padY;
    const W = w + padX * 2;
    const H = h + padY * 2;
    return x >= x1 && x <= x1 + W && y >= y1 && y <= y1 + H;
  }

  function selectIndex(i) {
    setLines((prev) => prev.map((l, idx) => ({ ...l, isSelected: idx === i })));
    setSelectedIndex(i);
  }
  function bringToFront(i) {
    setLines((prev) => {
      const next = [...prev];
      const [picked] = next.splice(i, 1);
      next.push(picked);
      setSelectedIndex(next.length - 1);
      return next.map((l, idx) => ({
        ...l,
        isSelected: idx === next.length - 1,
      }));
    });
  }
  function deleteSelected() {
    if (selectedIndex === null) return;
    setLines((prev) => prev.filter((_, i) => i !== selectedIndex));
    setSelectedIndex(null);
    setDragging({ mode: "none", end: null, lastX: 0, lastY: 0 });
  }

  // ======= Product helpers =======
  function currentProductFromTool() {
    return productFromToolName(tool, allProducts, activeTool);
  }

  function productColor(p) {
    return (
      p?.drawingDefaults?.color ??
      p?.visual ??
      p?.colorCode ??
      p?.color ??
      p?.defaultColor ??
      "#000000"
    );
  }

  function gutterProfileKeyFromProductName(name) {
    return normalizeGutterKey(name);
  }

  // ======= Drawing =======
  function drawAllLines(ctx) {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    drawGrid(ctx);
    for (const L of lines) drawLine(ctx, L);
    if (isDrawing) drawLine(ctx, currentLine);
  }

  function placeMeasurement(line, measurement, x, y) {
    const ctx = canvasRef.current.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.font = "900 12px Arial";
    ctx.textAlign = "center";
    ctx.fillStyle = "black";

    if (line.isHorizontal) {
      ctx.fillText(
        `${measurement}'`,
        x,
        y + (line.position === "top" ? -gridSize / 1.5 : gridSize * 1.5),
      );
    } else if (line.isVertical) {
      ctx.fillText(
        `${measurement}'`,
        x + (line.position === "left" ? -gridSize / 0.75 : gridSize * 1.25),
        y,
      );
    } else {
      ctx.fillText(`${measurement}'`, x, y - gridSize / 1.5);
    }
  }

  function drawLine(ctx, line, { isPreview = false } = {}) {
    const x1 = snap(line.startX);
    const y1 = snap(line.startY);
    const x2 = snap(line.endX);
    const y2 = snap(line.endY);

    // Annotation text
    if (line.isNote) {
      ctx.font = "1000 12px Arial";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.fillText(line.note, x1, y1);
      if (line.isSelected) {
        const w = ctx.measureText(line.note || "").width;
        const padX = 6;
        const padY = 4;
        const h = 12;
        ctx.save();
        ctx.strokeStyle = "orange";
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(
          x1 - w / 2 - padX,
          y1 - h - padY,
          w + padX * 2,
          h + padY * 2,
        );
        ctx.restore();
      }
      return;
    }

    // Downspout (priced)
    if (line.isDownspout) {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 + gridSize / 2.75, y1 + gridSize / 2.75);
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - gridSize / 2.75, y1 + gridSize / 2.75);
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 - gridSize / 2.75, y1 - gridSize / 2.75);
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 + gridSize / 2.75, y1 - gridSize / 2.75);
      ctx.strokeStyle = line.color || "#000";
      ctx.lineWidth = 2;
      ctx.stroke();

      const w = 60;
      const h = 28;

      // defaults if missing (for old diagrams)
      const angle =
        typeof line.elbowBoxAngle === "number"
          ? line.elbowBoxAngle
          : Math.PI / 4;
      const radius =
        typeof line.elbowBoxRadius === "number"
          ? line.elbowBoxRadius
          : gridSize * 5;

      // center of the label box relative to the X
      const cx = x1 + radius * Math.cos(angle);
      const cy = y1 + radius * Math.sin(angle);

      // top-left corner for drawing the rect
      const boxX = cx - w / 2;
      const boxY = cy - h / 2;

      ctx.fillStyle = "grey";
      ctx.fillRect(boxX, boxY, w, h);
      ctx.strokeStyle = line.color || "#000";
      ctx.strokeRect(boxX, boxY, w, h);

      // label: UPPERCASE sequence and footage
      const seq = String(line.elbowSequence || "").toUpperCase();
      const ft = Number.isFinite(Number(line.measurement))
        ? `${Number(line.measurement)}'`
        : ""; // or "0'" if you prefer always showing
      ctx.fillStyle = "white";
      ctx.textAlign = "center";

      // top line: sequence
      ctx.font = "10px Arial";
      ctx.fillText(seq, boxX + w / 2, boxY + 11);

      // bottom line: footage
      ctx.font = "10px Arial";
      ctx.fillText(ft, boxX + w / 2, boxY + 22);

      if (line.isSelected) {
        ctx.save();
        ctx.strokeStyle = "orange";
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(boxX - 4, boxY - 4, w + 8, h + 8);
        ctx.restore();
      }

      return;
    }

    // Splash Guard / Valley Shield (solid circle, non-priced)
    if (line.isFreeMark && line.kind === "free-circle" && line.fill) {
      const R = line.radius || gridSize * 0.8;
      ctx.beginPath();
      ctx.arc(x1, y1, R, 0, Math.PI * 2);
      ctx.fillStyle = line.color || "#111";
      ctx.fill();
      if (line.isSelected) {
        ctx.save();
        ctx.strokeStyle = "orange";
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(x1 - R - 6, y1 - R - 6, (R + 6) * 2, (R + 6) * 2);
        ctx.restore();
      }
      return;
    }

    // Free marks (line/square/hollow circle) — not priced
    // --- Free marks (free-line, free-square, free-circle) ---
    if (line.isFreeMark) {
      ctx.save();

      // stroke style
      ctx.lineWidth = line.strokeWidth ?? 2;
      ctx.strokeStyle = line.color ?? "#111";
      ctx.setLineDash(line.dashed ? [6, 4] : []);

      if (line.kind === "free-line") {
        // draw the line itself
        ctx.beginPath();
        ctx.moveTo(line.startX, line.startY);
        ctx.lineTo(line.endX, line.endY);
        ctx.stroke();

        // show edit handles when selected (same UX as gutters)
        if (line.isSelected) {
          const handleR = Math.max(
            MIN_HANDLE_PX,
            (typeof gridSize === "number" ? gridSize : 8) / 2,
          );
          ctx.setLineDash([]); // handles are solid
          ctx.fillStyle = "orange";

          // start handle
          ctx.beginPath();
          ctx.arc(line.startX, line.startY, handleR, 0, Math.PI * 2);
          ctx.fill();

          // end handle
          ctx.beginPath();
          ctx.arc(line.endX, line.endY, handleR, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (line.kind === "free-square") {
        const left = Math.min(line.startX, line.endX);
        const top = Math.min(line.startY, line.endY);
        const w = Math.abs(line.endX - line.startX);
        const h = Math.abs(line.endY - line.startY);
        if (line.fill) {
          ctx.fillStyle = line.color ?? "#111";
          ctx.fillRect(left, top, w, h);
        } else {
          ctx.strokeRect(left, top, w, h);
        }
      } else if (line.kind === "free-circle") {
        ctx.beginPath();
        ctx.arc(line.centerX, line.centerY, line.radius ?? 0, 0, Math.PI * 2);
        if (line.fill) {
          ctx.fillStyle = line.color ?? "#111";
          ctx.fill();
        } else {
          ctx.stroke();
        }
      }

      ctx.restore();
      return;
    }

    // Gutter line (priced)
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineWidth = 2;
    const stroke = line.isSelected
      ? "orange"
      : renderStrokeColor(line, { isPreview });

    ctx.strokeStyle = stroke;
    ctx.stroke();

    if (line.isSelected) {
      ctx.fillStyle = "orange";
      ctx.beginPath();
      ctx.arc(x1, y1, Math.max(MIN_HANDLE_PX, gridSize / 2), 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x2, y2, Math.max(MIN_HANDLE_PX, gridSize / 2), 0, Math.PI * 2);
      ctx.fill();
    }

    // Label position comes from the displayed endpoints.
    // Label value should use saved measurement when available.
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;

    const savedFeet = Math.round(Number(line.runFeet ?? line.measurement));

    const feetNow =
      !isPreview && Number.isFinite(savedFeet) && savedFeet > 0
        ? savedFeet
        : convertToFeet(calculateDistance([x1, y1], [x2, y2]));

    placeMeasurement(
      {
        ...line,
        isHorizontal: line.isHorizontal,
        isVertical: line.isVertical,
        position: line.position,
      },
      feetNow,
      midX,
      midY,
    );
  }

  async function saveCurrentDiagram() {
    const payload = buildDiagramPayload();
    // Prefer the host app’s provided callbacks; do not change their signatures
    try {
      if (
        selectedDiagram &&
        selectedDiagram._id &&
        typeof updateDiagram === "function"
      ) {
        await updateDiagram(selectedDiagram._id, payload);
      } else if (typeof addDiagramToProject === "function") {
        await addDiagramToProject(currentProjectId, payload);
      } else if (typeof handlePassDiagramData === "function") {
        // last-resort generic bridge used elsewhere in your app
        await handlePassDiagramData(payload);
      }
    } finally {
      // no modal/UX assumptions here
    }
  }

  // ======= Tools =======
  function handleToolSelectChange(e) {
    const nextTool = e.target.value;
    setTool(nextTool);
    setLines((prev) => prev.map((l) => ({ ...l, isSelected: false })));
    setSelectedIndex(null);

    // If switching to Free Line, ensure we don't accidentally carry the last product.
    if (nextTool === "freeLine") {
      setCurrentLine((prev) => ({
        ...prev,
        currentProduct: null,
        productId: null,
        isGutter: false,
      }));
    }
  }

  function handleAddDownspout(downspoutData) {
    const currentDownspout = (allProducts || []).find((p) => {
      return (
        /ownspout/i.test(p.name) &&
        p.name.toLowerCase().includes(downspoutData.profile.toLowerCase()) &&
        p.name.includes(downspoutData.downspoutSize.split(" ")[0])
      );
    });

    // ✅ Single, consistent DS color resolution (matches your edit branch intent)
    const dsColor =
      currentDownspout?.visual ||
      currentDownspout?.color ||
      currentDownspout?.colorCode ||
      currentDownspout?.defaultColor ||
      "#000";

    // If we came from clicking an existing DS box: update instead of creating a new line
    if (editingDownspoutIndex !== null) {
      const next = lines.map((ln, idx) => {
        if (idx !== editingDownspoutIndex) return ln;

        const ds = { ...ln };

        ds.elbowSequence = String(
          downspoutData.elbowSequence || "",
        ).toUpperCase();
        const tf = parseInt(downspoutData.totalFootage, 10);
        ds.totalFeet = isNaN(tf) ? 0 : tf;
        ds.measurement = ds.totalFeet;

        ds.price = currentDownspout?.price || ds.price || 0;

        // ✅ FIX: use dsColor (not just visual)
        ds.color = dsColor;

        ds.profile = downspoutData.profile;
        ds.downspoutSize = downspoutData.downspoutSize;

        ds.currentProduct = {
          price: currentDownspout?.price || 0,
          name: formatDownspoutName(
            downspoutData.downspoutSize,
            downspoutData.profile,
          ),
          description: currentDownspout?.description || "",

          // ✅ Helpful for downstream renderers (PDF/key/etc) if they ever inspect currentProduct
          color: dsColor,
          _id: currentDownspout?._id,
        };

        ds.rainBarrel = downspoutData.rainBarrel;
        ds.splashBlock = downspoutData.splashBlock;
        ds.undergroundDrainage = downspoutData.undergroundDrainage;

        return ds;
      });

      setLines(next);
      setIsDownspoutModalOpen(false);
      setEditingDownspoutIndex(null);

      const currentHash = hashLines(next);
      const hasChanged = currentHash !== baselineHashRef.current;

      if (hasChanged && selectedDiagram?._id) {
        setActiveModal("confirmDiagramOverwrite");
      } else {
        setActiveModal("diagram");
      }
      return;
    }

    const formatted = {
      id: newId(),
      startX: downspoutCoordinates[0],
      startY: downspoutCoordinates[1],
      endX: downspoutCoordinates[0],
      endY: downspoutCoordinates[1],
      midpoint: null,
      totalFeet: Number.parseInt(downspoutData.totalFootage, 10) || 0,
      measurement: Number.parseInt(downspoutData.totalFootage, 10) || 0,

      // ✅ FIX: this was the bug
      color: dsColor,

      isSelected: false,
      isDownspout: true,
      profile: downspoutData.profile,
      price: currentDownspout?.price || 0,
      elbowSequence: downspoutData.elbowSequence,
      downspoutSize: downspoutData.downspoutSize,
      currentProduct: {
        price: currentDownspout?.price || 0,
        name: formatDownspoutName(
          downspoutData.downspoutSize,
          downspoutData.profile,
        ),
        description: currentDownspout?.description || "",
        color: dsColor,
        _id: currentDownspout?._id,
      },
      rainBarrel: downspoutData.rainBarrel,
      splashBlock: downspoutData.splashBlock,
      undergroundDrainage: downspoutData.undergroundDrainage,
      elbowBoxAngle: Math.PI / 4,
      elbowBoxRadius: gridSize * 5,
    };

    setLines((prev) => [...prev, formatted]);
    setIsDownspoutModalOpen(false);
    setEditingDownspoutIndex(null);
    setActiveModal("diagram");

    requestAnimationFrame(() => {
      setCanvasSize();
      drawScene();
    });
  }

  function addNote(note) {
    const n = {
      id: newId(),
      startX: noteCoordinates[0],
      startY: noteCoordinates[1],
      endX: noteCoordinates[0],
      endY: noteCoordinates[1],
      isSelected: false,
      isNote: true,
      note,
      color: "black",
    };

    setLines((prev) => [...prev, n]);
    setEditingNoteIndex(null);
    setActiveModal("diagram");

    requestAnimationFrame(() => {
      setCanvasSize();
      drawScene();
    });
  }
  // ======= Pointer handlers =======
  function handleMouseDown(e) {
    if (isDownspoutModalOpen) return;

    const { x, y } = getCanvasCoords(e);
    const snappedX = snap(x);
    const snappedY = snap(y);

    // Downspout
    if (tool === "downspout") {
      setDownspoutCoordinates([snappedX, snappedY]);
      setIsDownspoutModalOpen(true);
      setActiveModal("downspout");
      return;
    }

    // Note
    if (tool === "note") {
      setNoteCoordinates([snappedX, snappedY]);
      setActiveModal("note");
      return;
    }

    // Splash Guard / Valley Shield (solid circle mark, priced)
    // --- Splash Guard (priced mark, filled circle) ---
    if (tool === "splashGuard") {
      // Find the Splash Guard product from your catalog
      const prod =
        (Array.isArray(allProducts) ? allProducts : []).find((p) =>
          /splash\s*guard/i.test(String(p?.name || "")),
        ) || null;

      // Resolve its display color (prefer explicit color fields from product)
      const col =
        prod?.color ??
        prod?.visual ??
        prod?.colorCode ??
        prod?.defaultColor ??
        "#111111";

      // Commit a priced, filled-circle mark; still a "free" shape for canvas hit-tests
      const mark = {
        id: newId(),
        isFreeMark: true, // still a free mark for UI behavior
        isSplashGuard: true, // <-- SIGNAL this is a priced item
        kind: "free-circle",
        fill: true,
        color: col, // <-- draw with product color
        dashed: false,
        strokeWidth: 2,
        centerX: snappedX,
        centerY: snappedY,
        startX: snappedX,
        startY: snappedY,
        radius: 4,
        isSelected: false,

        // Freeze a pricing snapshot for line items
        currentProduct: prod
          ? {
              _id: prod._id,
              name: prod.name,
              price: Number(prod.price || 0),
              color: col,
              description: prod.description || "",
              profile: prod.profile || "",
              size: prod.size || "",
            }
          : null,
        price: Number(prod?.price || 0),
      };

      setLines((prev) => [...prev, mark]);
      return;
    }

    // Select
    // Select
    if (tool === "select") {
      const now = Date.now();
      const sameSpot =
        Math.hypot(
          selectCycleRef.current.x - x,
          selectCycleRef.current.y - y,
        ) <= Math.max(6, gridSize * 0.25);
      const withinWindow = now - selectCycleRef.current.time < 800; // ms between clicks

      // Compute stack at this click location when first click or moved
      if (!sameSpot || !withinWindow) {
        selectCycleRef.current.stack = getHitStackAt(x, y);
        selectCycleRef.current.idx = -1;
      }

      selectCycleRef.current.time = now;
      selectCycleRef.current.x = x;
      selectCycleRef.current.y = y;

      const stack = selectCycleRef.current.stack;
      if (!stack.length) {
        setLines((prev) => prev.map((l) => ({ ...l, isSelected: false })));
        setSelectedIndex(null);
        setDragging({ mode: "none", end: null, lastX: 0, lastY: 0 });
        dragOriginRef.current = null;
        return;
      }

      // Advance to next item in the stack (cycle)
      selectCycleRef.current.idx =
        (selectCycleRef.current.idx + 1) % stack.length;

      const hitIndex = stack[selectCycleRef.current.idx];
      const el = lines[hitIndex];

      // Mark selection
      setLines((prev) =>
        prev.map((l, idx) => ({ ...l, isSelected: idx === hitIndex })),
      );
      setSelectedIndex(hitIndex);

      // Decide initial drag mode for the selected element
      let dragMode = "none";
      let end = null;

      if (el.isNote) {
        dragMode = "move";
      } else if (el.isDownspout) {
        // 👇 NEW: differentiate box vs center
        const partHit = hitTestDownspout(el, x, y); // {part: "box"} | {part: "center"} | null
        if (partHit?.part === "box") {
          dragMode = "move-box";
          boxClickRef.current = { x, y, index: hitIndex }; // track for click-vs-drag -> open modal
        } else {
          dragMode = "move"; // dragging the X
        }
      } else if (el.isFreeMark) {
        if (el.kind === "free-line") {
          const h = hitTestLine(el, x, y);

          if (h?.hit === "start" || h?.hit === "end") {
            dragMode = "drag-end";
            end = h.hit; // "start" | "end"
          } else {
            dragMode = "move";
          }
        } else if (el.kind === "free-square") {
          dragMode = "move-free-square";
        } else if (el.kind === "free-circle") {
          dragMode = "move-free-circle";
        }
      } else {
        // gutter line: allow move or end-drag depending on where we clicked
        const h = hitTestLine(el, x, y);
        if (h?.hit === "start" || h?.hit === "end") {
          dragMode = "drag-end";
          end = h.hit; // "start" | "end"
        } else {
          dragMode = "move";
        }
      }

      dragOriginRef.current = {
        pointerX: x,
        pointerY: y,
        index: hitIndex,
        mode: dragMode,
        end,
        line:
          typeof structuredClone === "function"
            ? structuredClone(el)
            : JSON.parse(JSON.stringify(el)),
      };

      setDragging({ mode: dragMode, end, lastX: x, lastY: y });
      return;
    }

    // Free Line / Square / Circles (non-priced)
    // Free Line / Square / Circles (non-priced)
    // Free Line / Square / Circles (non-priced)
    if (tool === "freeLine") {
      // Keep mini-UI selections
      const base = {
        ...currentLine,
        id: newId(),
        isFreeMark: true,

        // Free marks must NOT carry product context.
        currentProduct: null,
        productId: null,
        isGutter: false,

        color: currentLine.color || "#111111",
        dashed: !!currentLine.dashed,
        strokeWidth: currentLine.strokeWidth ?? 2,
        isSelected: false,
        kind: currentLine.kind || "free-line",
        fill: currentLine.fill,
        radius: currentLine.radius,
      };
      if (base.kind === "free-line") {
        // drag-to-draw line
        setCurrentLine({
          ...base,
          startX: snappedX,
          startY: snappedY,
          endX: snappedX,
          endY: snappedY,
        });
        setIsDrawing(true);
        return;
      }

      if (base.kind === "free-square") {
        // CLICK-TO-PLACE: static square
        const size = Math.max(16, gridSize);
        const half = Math.floor(size / 2);
        const sq = {
          ...base,
          startX: snappedX - half,
          startY: snappedY - half,
          endX: snappedX + half,
          endY: snappedY + half,
          fill: !!base.fill,
        };
        setLines((prev) => [...prev, sq]);
        return; // no drawing session
      }

      if (base.kind === "free-circle") {
        // CLICK-TO-PLACE: static circle
        const R = base.radius ?? Math.max(4, Math.floor(gridSize));
        const circ = {
          ...base,
          centerX: snappedX,
          centerY: snappedY,
          radius: R / 2,
          startX: snappedX, // harmless for other code
          startY: snappedY,
          fill: !!base.fill,
        };
        setLines((prev) => [...prev, circ]);
        return; // no drawing session
      }

      // fallback to line
      setCurrentLine({
        ...base,
        kind: "free-line",
        startX: snappedX,
        startY: snappedY,
        endX: snappedX,
        endY: snappedY,
      });
      setIsDrawing(true);
      return;
    }

    // ...inside handleMouseDown(e) when starting a priced (gutter) line
    const baseDraft = {
      id: newId(),
      startX: snappedX,
      startY: snappedY,
      endX: snappedX,
      endY: snappedY,
      isSelected: false,
      color: "black",
      isVertical: false,
      isHorizontal: false,
    };
    const stamped = applyCatalogToolMetadata(
      baseDraft,
      activeTool,
      tool,
      allProducts,
    );

    setCurrentLine(stamped);
    setIsDrawing(true);
  }

  function handleMouseMove(e) {
    if (isDownspoutModalOpen) return;

    const { x, y } = getCanvasCoords(e);
    const sx = snap(x);
    const sy = snap(y);

    // --- SELECTION / MOVEMENT HANDLING ---
    if (tool === "select" && dragging.mode !== "none") {
      const origin = dragOriginRef.current;

      if (!origin || origin.index == null) {
        return;
      }

      const dragIndex = origin.index;

      const dx = sx - snap(origin.pointerX);
      const dy = sy - snap(origin.pointerY);

      setLines((prev) => {
        const next = [...prev];
        const original = origin.line;
        if (!original) return prev;

        const el = { ...original };
        if (!next[dragIndex]) return prev;
        if (el.isNote) {
          el.startX = original.startX + dx;
          el.startY = original.startY + dy;
          el.endX = el.startX;
          el.endY = el.startY;
        } else if (el.isDownspout) {
          if (dragging.mode === "move-box") {
            const angle = Math.atan2(y - original.startY, x - original.startX);
            el.elbowBoxAngle = angle;
            el.elbowBoxRadius =
              typeof original.elbowBoxRadius === "number"
                ? original.elbowBoxRadius
                : gridSize * 4;
          } else {
            el.startX = original.startX + dx;
            el.startY = original.startY + dy;
            el.endX = el.startX;
            el.endY = el.startY;
          }
        } else if (el.isFreeMark) {
          if (el.kind === "free-line") {
            if (dragging.mode === "move") {
              el.startX = original.startX + dx;
              el.startY = original.startY + dy;
              el.endX = original.endX + dx;
              el.endY = original.endY + dy;
            }

            if (dragging.mode === "drag-end") {
              if (dragging.end === "start") {
                el.startX = sx;
                el.startY = sy;
              }

              if (dragging.end === "end") {
                el.endX = sx;
                el.endY = sy;
              }
            }

            // Keep these so hit testing and rendering stay stable
            el.midpoint = [
              (el.startX + el.endX) / 2,
              (el.startY + el.endY) / 2,
            ];
          } else if (el.kind === "free-square") {
            el.startX = original.startX + dx;
            el.startY = original.startY + dy;
            el.endX = original.endX + dx;
            el.endY = original.endY + dy;
          } else if (el.kind === "free-circle") {
            el.centerX = snap(original.centerX + dx);
            el.centerY = snap(original.centerY + dy);
            el.startX = el.centerX;
            el.startY = el.centerY;
          }
        } else {
          if (dragging.mode === "move") {
            el.startX = original.startX + dx;
            el.startY = original.startY + dy;
            el.endX = original.endX + dx;
            el.endY = original.endY + dy;
          } else if (dragging.mode === "drag-end") {
            if (dragging.end === "start") {
              el.startX = sx;
              el.startY = sy;
            } else {
              el.endX = sx;
              el.endY = sy;
            }
          }

          updateLineComputedProps(el);
        }

        next[dragIndex] = {
          ...el,
          isSelected: true,
        };

        return next;
      });

      setDragging((prev) => ({ ...prev, lastX: x, lastY: y }));
      return;
    }

    // --- DRAWING HANDLING ---
    if (
      isDrawing &&
      tool !== "downspout" &&
      tool !== "select" &&
      tool !== "note"
    ) {
      if (tool === "freeLine") {
        if ((currentLine.kind || "free-line") === "free-line") {
          setCurrentLine((prev) => ({ ...prev, endX: sx, endY: sy }));
        }
        return;
      }

      // Straight-line guidance coloring
      if (
        isLineParallelToSide(
          currentLine.startX,
          currentLine.startY,
          currentLine.endX,
          currentLine.endY,
        ) ||
        isLineParallelToTop(
          currentLine.startX,
          currentLine.startY,
          currentLine.endX,
          currentLine.endY,
        )
      ) {
        setCurrentLine((prevLine) => ({
          ...prevLine,
          endX: sx,
          endY: sy,
          color: "#14c414",
        }));
      } else {
        setCurrentLine((prevLine) => ({
          ...prevLine,
          endX: sx,
          endY: sy,
          color: "black",
        }));
      }

      const pt1 = [currentLine.startX, currentLine.startY];
      const pt2 = [sx, sy];
      setLineLength(convertToFeet(calculateDistance(pt1, pt2)));
    }
  }

  function handleMouseUp() {
    dragOriginRef.current = null;
    // open modal if we "clicked" the DS box (not dragged it)
    if (tool === "select" && dragging.mode === "move-box") {
      const { x, y } = boxClickRef.current;
      const rect = canvasRef.current.getBoundingClientRect();
      const up = getCanvasCoords({
        nativeEvent: { clientX: dragging.lastX, clientY: dragging.lastY },
      });
      const moved = Math.hypot(up.x - x, up.y - y);
      // threshold ~4px
      if (moved < 4 && selectedIndex !== null) {
        const ds = lines[selectedIndex];
        if (ds?.isDownspout) {
          setEditingDownspoutIndex(selectedIndex);
          setDownspoutCoordinates([ds.startX, ds.startY]);
          setActiveModal("downspout");
        }
      }
      // reset drag state
      setDragging({ mode: "none", end: null, lastX: 0, lastY: 0 });
      setIsDrawing(false);
      return;
    }

    if (isDownspoutModalOpen) return;

    // open AnnotationModal if we "clicked" a note (not dragged it)
    if (tool === "select" && noteClickRef.current.index !== null) {
      const { x: downX, y: downY, index } = noteClickRef.current;
      const upXY = getCanvasCoords({
        nativeEvent: { clientX: dragging.lastX, clientY: dragging.lastY },
      });
      const moved = Math.hypot(upXY.x - downX, upXY.y - downY);
      // small threshold => treat as click
      if (moved < 4) {
        const noteLine = lines[index];
        if (noteLine?.isNote) {
          setEditingNoteIndex(index);
          setActiveModal("note"); // reuse your existing modal slot
        }
      }
      // reset the click ref
      noteClickRef.current = { x: 0, y: 0, index: null };
    }

    if (tool === "select") {
      setDragging({ mode: "none", end: null, lastX: 0, lastY: 0 });
      setIsDrawing(false);
      return;
    }
    if (tool === "note" || tool === "downspout") {
      setIsDrawing(false);
      return;
    }

    // Free tool commit
    // Free tool commit
    if (tool === "freeLine" && isDrawing) {
      const c = currentLine;
      if (!c) {
        setIsDrawing(false);
        return;
      }

      // Only commit if we were drawing a line; circle/square are click-to-place
      if ((c.kind || "free-line") !== "free-line") {
        setIsDrawing(false);
        return;
      }

      if (c.startX === c.endX && c.startY === c.endY) {
        setIsDrawing(false);
        return;
      }

      // Ensure dashed/color persist to committed element
      setLines((prev) => [
        ...prev,
        {
          ...c,
          dashed: !!c.dashed,
          color: c.color || "#111111",
          isFreeMark: true,

          // Free marks must NOT carry product context.
          currentProduct: null,
          productId: null,
          isGutter: false,
        },
      ]);

      setIsDrawing(false);
      setLineLength(0);
      setCurrentLine((prev) => ({
        ...prev,
        id: null,
        startX: 0,
        startY: 0,
        endX: 0,
        endY: 0,
      }));
      return;
    }

    /* // Commit gutter line (priced)
    if (isDrawing) {
      const prod = currentProductFromTool();
      const committed = { ...currentLine };

      committed.midpoint = [
        (committed.startX + committed.endX) / 2,
        (committed.startY + committed.endY) / 2,
      ];
      committed.measurement = convertToFeet(
        calculateDistance(
          [committed.startX, committed.startY],
          [committed.endX, committed.endY]
        )
      );
      updateLineComputedProps(committed);
      committed.currentProduct = prod || null;
      committed.color = prod ? productColor(prod) : "black";

      if (
        committed.startX === committed.endX &&
        committed.startY === committed.endY
      ) {
        setIsDrawing(false);
        setLineLength(0);
        return;
      }

      setLines((prev) => [...prev, committed]);
      setIsDrawing(false);
      setLineLength(0);
    } */
    if (isDrawing) {
      const prod = currentProductFromTool(); // your existing helper
      const committed = { ...currentLine };

      // --- geometry first ---
      committed.midpoint = [
        (committed.startX + committed.endX) / 2,
        (committed.startY + committed.endY) / 2,
      ];
      committed.measurement = convertToFeet(
        calculateDistance(
          [committed.startX, committed.startY],
          [committed.endX, committed.endY],
        ),
      );

      // --- STAMP (conservatively) based on the product currently selected ---
      if (prod) {
        const isGutterProd =
          /gutter/i.test(String(prod.name || "")) ||
          String(prod.category || "").toLowerCase() === "gutters" ||
          String(prod.toolGroup || "").toLowerCase() === "gutters" ||
          String(prod.assembly?.type || "")
            .toLowerCase()
            .includes("gutter");

        if (isGutterProd) {
          committed.isGutter = true;

          const px = distancePxOf(committed);
          committed.runFeet = roundToQuarterFeet(
            feetFromPx(px, feetPerSquare, gridSize),
          );

          if (!committed.currentProduct) committed.currentProduct = prod;
          if (!committed.profileKey) committed.profileKey = prod.profile || "";
          if (!committed.sizeInches) committed.sizeInches = prod.size || "";

          committed.productId =
            committed.productId || prod._id || prod.id || null;
          committed.price = Number(prod.price ?? prod.pricing?.unitPrice ?? 0);
          committed.color =
            productColor(committed.currentProduct || prod) || "#000000";
        } else {
          if (!committed.currentProduct) committed.currentProduct = prod;

          committed.productId =
            committed.productId || prod._id || prod.id || null;
          committed.price = Number(prod.price ?? prod.pricing?.unitPrice ?? 0);
          committed.color = productColor(prod) || committed.color || "#000000";
        }
      } else {
        if (!committed.color) committed.color = "#000000";
      }

      // --- compute any derived props that might rely on meta (runFeet, flags, etc.) ---
      updateLineComputedProps(committed);
      // compute runFeet from px using current scale; round to quarter foot
      if (committed.isGutter) {
        const pxDist = calculateDistance(
          [committed.startX, committed.startY],
          [committed.endX, committed.endY],
        );
        const ft =
          Number(pxDist || 0) *
          (Number(feetPerSquare || 1) / Number(gridSize || 1));
        committed.runFeet = Math.round(ft * 4) / 4;
      }

      // reject zero-length lines
      if (
        committed.startX === committed.endX &&
        committed.startY === committed.endY
      ) {
        setIsDrawing(false);
        setLineLength(0);
        return;
      }

      setLines((prev) => [...prev, committed]);
      setIsDrawing(false);
      setLineLength(0);
    }
  }

  // ======= Analyze joints (end caps & miters) =======
  function centroidOfLines(all) {
    if (!all.length) return { x: 0, y: 0 };
    let sx = 0,
      sy = 0;
    all.forEach((L) => {
      sx += (L.startX + L.endX) / 2;
      sy += (L.startY + L.endY) / 2;
    });
    return { x: sx / all.length, y: sy / all.length };
  }
  const JOINT_SNAP = 1;
  const keyForPoint = (x, y, tol = JOINT_SNAP) =>
    `${Math.round(x / tol) * tol}|${Math.round(y / tol) * tol}`;
  const len = (v) => Math.hypot(v.x, v.y);
  const norm = (v) => {
    const L = len(v) || 1;
    return { x: v.x / L, y: v.y / L };
  };
  const dot = (a, b) => a.x * b.x + a.y * b.y;
  const angleBetweenDeg = (a, b) => {
    const A = norm(a),
      B = norm(b);
    const c = Math.min(1, Math.max(-1, dot(A, B)));
    return (Math.acos(c) * 180) / Math.PI;
  };
  const cornerBisector = (vA, vB) => {
    const A = norm(vA),
      B = norm(vB);
    const bx = A.x + B.x,
      by = A.y + B.y;
    const L = Math.hypot(bx, by);
    if (L < 1e-6) return { x: 0, y: 0 };
    return { x: bx / L, y: by / L };
  };
  const isStraight = (ang) => ang > 175;
  const isRight = (ang) => ang >= 80 && ang <= 100;
  const isBay = (ang) => ang >= 130 && ang <= 150;

  function analyzeJoints(allLines) {
    const gutters = allLines
      .filter(
        (L) => !L.isNote && !L.isDownspout && !L.isFreeMark && L.currentProduct,
      )
      .map((L) => ({
        L,
        key: gutterProfileKeyFromProductName(L.currentProduct.name),
      }));

    const center = centroidOfLines(gutters.map((g) => g.L));
    const joints = new Map();

    gutters.forEach(({ L, key }, idx) => {
      const sK = keyForPoint(L.startX, L.startY);
      const eK = keyForPoint(L.endX, L.endY);
      if (!joints.has(sK))
        joints.set(sK, { x: L.startX, y: L.startY, members: [] });
      if (!joints.has(eK))
        joints.set(eK, { x: L.endX, y: L.endY, members: [] });
      joints.get(sK).members.push({ lineIndex: idx, end: "start", key });
      joints.get(eK).members.push({ lineIndex: idx, end: "end", key });
    });

    const endCapsByProduct = Object.create(null);
    const miters = Object.create(null);
    const mixed = Object.create(null);

    const ensure = (obj, k) =>
      obj[k] ||
      (obj[k] = { inside90: 0, outside90: 0, bay135: 0, custom: new Map() });

    joints.forEach((joint) => {
      const { x: JX, y: JY, members } = joint;
      const degree = members.length;

      if (degree === 1) {
        const k = members[0].key;
        endCapsByProduct[k] = (endCapsByProduct[k] || 0) + 1;
        return;
      }
      if (degree < 2) return;

      const rays = members
        .map(({ lineIndex, end, key }) => {
          const L = gutters[lineIndex].L;
          const ox = end === "start" ? L.endX : L.startX;
          const oy = end === "start" ? L.endY : L.startY;
          return {
            key,
            v: { x: ox - JX, y: oy - JY },
            angle: Math.atan2(oy - JY, ox - JX),
          };
        })
        .filter((r) => len(r.v) > 0.0001)
        .sort((a, b) => a.angle - b.angle);

      const pairs =
        rays.length === 2
          ? [[rays[0], rays[1]]]
          : rays.map((r, i) => [r, rays[(i + 1) % rays.length]]);
      const toC = { x: center.x - JX, y: center.y - JY };

      pairs.forEach(([A, B]) => {
        const ang = angleBetweenDeg(A.v, B.v);
        if (isStraight(ang)) return;

        const bis = cornerBisector(A.v, B.v);
        const facesCenter = bis.x * toC.x + bis.y * toC.y > 0;
        let label = "custom";
        if (isRight(ang)) label = facesCenter ? "inside90" : "outside90";
        else if (isBay(ang)) label = "bay135";

        if (A.key === B.key) {
          const b = ensure(miters, A.key);
          if (label === "inside90") b.inside90 += 1;
          else if (label === "outside90") b.outside90 += 1;
          else if (label === "bay135") b.bay135 += 1;
          else
            b.custom.set(
              Math.round(ang),
              (b.custom.get(Math.round(ang)) || 0) + 1,
            );
        } else {
          const combo = [A.key, B.key].sort().join(" + ");
          const b = ensure(mixed, combo);
          if (label === "inside90") b.inside90 += 1;
          else if (label === "outside90") b.outside90 += 1;
          else if (label === "bay135") b.bay135 += 1;
          else
            b.custom.set(
              Math.round(ang),
              (b.custom.get(Math.round(ang)) || 0) + 1,
            );
        }
      });
    });

    const finalize = (obj) => {
      const out = {};
      Object.entries(obj).forEach(([k, v]) => {
        out[k] = {
          inside90: v.inside90,
          outside90: v.outside90,
          bay135: v.bay135,
          custom: [...v.custom.entries()].map(([angle, count]) => ({
            angle,
            count,
          })),
        };
      });
      return out;
    };

    return {
      endCapsByProduct,
      mitersByProduct: finalize(miters),
      mixedMiters: finalize(mixed),
    };
  }

  // --- Touch → Mouse delegators so mobile can draw & commit ---
  function handleTouchStart(e) {
    // e.preventDefault(); // keep the page from scrolling
    handleMouseDown(e); // your getCanvasCoords already reads touches
  }
  function handleTouchMove(e) {
    // e.preventDefault();
    handleMouseMove(e);
  }
  function handleTouchEnd(e) {
    // e.preventDefault();
    handleMouseUp(e);
  }
  function hardUnlockScroll() {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }

  function unlockPageScroll() {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    document.body.style.touchAction = "";
    document.documentElement.style.touchAction = "";
  }

  function unscaleLineForSave(line) {
    const transform = displayTransformRef.current || {};
    const sx = Number(transform.sx) || 1;
    const sy = Number(transform.sy) || 1;
    const kAvg = Number(transform.kAvg) || 1;

    const copy = { ...line };

    const unscaleX = (value) =>
      typeof value === "number" && Number.isFinite(value) ? value / sx : value;

    const unscaleY = (value) =>
      typeof value === "number" && Number.isFinite(value) ? value / sy : value;

    const unscaleAvg = (value) =>
      typeof value === "number" && Number.isFinite(value)
        ? value / kAvg
        : value;

    if (copy.startX != null) copy.startX = unscaleX(copy.startX);
    if (copy.endX != null) copy.endX = unscaleX(copy.endX);
    if (copy.centerX != null) copy.centerX = unscaleX(copy.centerX);

    if (copy.startY != null) copy.startY = unscaleY(copy.startY);
    if (copy.endY != null) copy.endY = unscaleY(copy.endY);
    if (copy.centerY != null) copy.centerY = unscaleY(copy.centerY);

    if (copy.radius != null) copy.radius = unscaleAvg(copy.radius);
    if (copy.elbowBoxRadius != null) {
      copy.elbowBoxRadius = unscaleAvg(copy.elbowBoxRadius);
    }

    if (Array.isArray(copy.midpoint)) {
      copy.midpoint = [unscaleX(copy.midpoint[0]), unscaleY(copy.midpoint[1])];
    }

    return copy;
  }

  // ======= Save diagram =======
  // ⬇️ drop in place of your current saveDiagram
  async function saveDiagram(saveType) {
    setSelectedIndex(null);

    // --- mobile/iOS safety: draw then yield a frame before reading canvas ---
    const raf = () => new Promise((res) => requestAnimationFrame(res));
    async function ensureCanvasPainted(ctx, drawFn) {
      drawFn(ctx);
      await raf(); // let the GPU present before we read pixels / toDataURL
    }

    function foldAccessoryItems(items) {
      const map = new Map();

      (items || []).forEach((it) => {
        const isAccessory =
          it?.meta &&
          ["miter", "endcap", "elbow", "offset"].includes(it.meta.kind);

        // For accessories: do NOT rely on product._id. Use meta to preserve intent.
        // For base rows (gutters/downspouts), product id/name is fine.
        const key = isAccessory
          ? [
              it.meta?.kind || "", // miter | endcap | elbow | offset
              it.meta?.miterType || "", // strip | bay | custom
              it.meta?.degrees ?? "", // number for custom miters
              it.meta?.code || "", // A | B for elbows
              it.meta?.inches || "", // 2 | 4 | 6 for offsets
              it.meta?.size || it.meta?.sizeLabel || "",
              it.meta?.profileKey || it.meta?.profile || "",
              it.name || "", // tie-breaker so renamed customs stay separate
            ].join("|")
          : it.product?._id || it.name || "base";

        const prev = map.get(key);
        if (prev) {
          prev.quantity = Number(prev.quantity || 0) + Number(it.quantity || 0);
        } else {
          map.set(key, { ...it, quantity: Number(it.quantity || 0) });
        }
      });

      return Array.from(map.values());
    }

    // Require content to save
    if (lines.length === 0) {
      alert("Add at least one line before saving.");
      return;
    }

    // Only enforce "no changes" for existing diagrams
    const isExisting = Boolean(selectedDiagram?._id);
    const currentHash = hashLines(lines);
    if (isExisting) {
      const hasChanged = currentHash !== baselineHashRef.current;
      if (!hasChanged) {
        alert("No changes to save.");
        return;
      }
    }

    // Require a project id
    const resolvedProjectId =
      currentProjectId ||
      selectedDiagram?.projectId ||
      originalDiagram?.projectId ||
      null;
    if (!resolvedProjectId) {
      alert("Please select a project before saving a diagram.");
      return;
    }

    // --- thumb bounds for ALL elements (lines, notes, free marks, downspouts) ---
    function boundsUnion(a, b) {
      return {
        minX: Math.min(a.minX, b.minX),
        minY: Math.min(a.minY, b.minY),
        maxX: Math.max(a.maxX, b.maxX),
        maxY: Math.max(a.maxY, b.maxY),
      };
    }
    function elementBounds(el, ctx) {
      let box = {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
      };

      if (!el.isFreeMark && !el.isNote && !el.isDownspout) {
        return {
          minX: Math.min(el.startX, el.endX),
          minY: Math.min(el.startY, el.endY),
          maxX: Math.max(el.startX, el.endX),
          maxY: Math.max(el.startY, el.endY),
        };
      }

      if (el.isNote) {
        const text = String(el.note || "");
        ctx.save();
        ctx.font = "1000 12px Arial";
        const width = ctx.measureText(text).width;
        ctx.restore();
        const paddingX = 6;
        const paddingY = 4;
        const height = 12;
        const x1 = el.startX - width / 2 - paddingX;
        const y1 = el.startY - height - paddingY;
        return {
          minX: x1,
          minY: y1,
          maxX: x1 + width + paddingX * 2,
          maxY: y1 + height + paddingY * 2,
        };
      }

      if (el.isDownspout) {
        const r = gridSize;
        const cross = {
          minX: el.startX - r,
          minY: el.startY - r,
          maxX: el.startX + r,
          maxY: el.startY + r,
        };
        const w = 60;
        const h = 28; // keep in sync with draw & hitTest
        const angle =
          typeof el.elbowBoxAngle === "number" ? el.elbowBoxAngle : Math.PI / 4;
        const radius =
          typeof el.elbowBoxRadius === "number"
            ? el.elbowBoxRadius
            : gridSize * 5;
        const cx = el.startX + radius * Math.cos(angle);
        const cy = el.startY + radius * Math.sin(angle);
        const box = {
          minX: cx - w / 2,
          minY: cy - h / 2,
          maxX: cx + w / 2,
          maxY: cy + h / 2,
        };
        return boundsUnion(cross, box);
      }

      if (el.isFreeMark) {
        if (el.kind === "free-line") {
          return {
            minX: Math.min(el.startX, el.endX),
            minY: Math.min(el.startY, el.endY),
            maxX: Math.max(el.startX, el.endX),
            maxY: Math.max(el.startY, el.endY),
          };
        }
        if (el.kind === "free-square") {
          const left = Math.min(el.startX, el.endX);
          const top = Math.min(el.startY, el.endY);
          const w = Math.abs(el.endX - el.startX);
          const h = Math.abs(el.endY - el.startY);
          return { minX: left, minY: top, maxX: left + w, maxY: top + h };
        }
        if (el.kind === "free-circle") {
          const r = Math.max(0, el.radius || 0);
          return {
            minX: el.centerX - r,
            minY: el.centerY - r,
            maxX: el.centerX + r,
            maxY: el.centerY + r,
          };
        }
      }

      const x = el.startX ?? el.centerX ?? 0;
      const y = el.startY ?? el.centerY ?? 0;
      return { minX: x, minY: y, maxX: x, maxY: y };
    }
    function getBoundingBoxForAll(all, ctx, padding = 20) {
      let box = {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
      };
      all.forEach((el) => {
        const b = elementBounds(el, ctx);
        box = boundsUnion(box, b);
      });
      if (box.minX === Infinity)
        box = { minX: 0, minY: 0, maxX: 100, maxY: 100 };
      return {
        minX: box.minX - padding,
        minY: box.minY - padding,
        maxX: box.maxX + padding,
        maxY: box.maxY + padding,
      };
    }

    const token = localStorage.getItem("jwt");
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // ⬇️ draw + yield one frame before reading back pixels (mobile fix)
    await ensureCanvasPainted(ctx, drawAllLines);

    const dpr = window.devicePixelRatio || 1;

    // 1) Compute bounds in CSS units (helper returns CSS units)
    const boundingBox = getBoundingBoxForAll(lines, ctx, 40); // a little more padding

    // 2) Convert CSS -> backing store pixels
    const srcX = Math.max(0, Math.floor(boundingBox.minX * dpr));
    const srcY = Math.max(0, Math.floor(boundingBox.minY * dpr));
    const srcW = Math.min(
      Math.floor((boundingBox.maxX - boundingBox.minX) * dpr),
      canvas.width - srcX,
    );
    const srcH = Math.min(
      Math.floor((boundingBox.maxY - boundingBox.minY) * dpr),
      canvas.height - srcY,
    );

    // ... setup tempCanvas ...
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d");

    const thumbnailDisplaySize = 180;
    const thumbnailInternalSize = 2 * thumbnailDisplaySize; // 360px square is plenty

    // fixed, crisp thumb regardless of device
    tempCanvas.width = thumbnailInternalSize;
    tempCanvas.height = thumbnailInternalSize;
    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

    // leave some border inside thumb so labels/joins never touch edges
    const pad = 0.05 * thumbnailInternalSize;
    const availW = thumbnailInternalSize - pad * 2;
    const availH = thumbnailInternalSize - pad * 2;

    // destination size computed in CSS pixels first, then mapped 1:1 to thumb
    const cropWidthCss = boundingBox.maxX - boundingBox.minX;
    const cropHeightCss = boundingBox.maxY - boundingBox.minY;
    const scale = Math.min(availW / cropWidthCss, availH / cropHeightCss);
    const destW = cropWidthCss * scale;
    const destH = cropHeightCss * scale;

    const dx = (thumbnailInternalSize - destW) / 2;
    const dy = (thumbnailInternalSize - destH) / 2;

    // white background
    // --- DEV-ONLY logging for vector vs raster thumbnail path ---
    if (true) {
      // Vector is possible if we at least have line geometry.
      const vectorPossible = Array.isArray(lines) && lines.length > 0;
      if (vectorPossible) {
        console.info(
          "[THUMBNAIL] VECTOR data available (lines present). Using RASTER path currently.",
        );
      } else {
        console.info(
          "[THUMBNAIL] Falling back to RASTER thumbnail → PNG/JPEG.",
        );
        const missing = [];
        if (!Array.isArray(lines) || lines.length === 0)
          missing.push("lines[]");
        console.debug("[THUMBNAIL] Missing geometry data:", missing);
      }
    }

    tempCtx.fillStyle = "#ffffff";
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // draw from the hi-DPI source rect
    tempCtx.drawImage(canvas, srcX, srcY, srcW, srcH, dx, dy, destW, destH);

    setSelectedIndex(null);
    setLines((prev) =>
      prev.map((l) => (l.isSelected ? { ...l, isSelected: false } : l)),
    );

    const thumbnailDataUrl = await canvasToDataURLAsync(
      tempCanvas,
      "image/png",
      0.92,
    );

    // --- PRICE + ACCESSORIES ---
    let price = 0;
    let totalFootage = 0;

    lines.forEach((line) => {
      if (line.isDownspout) {
        const p = Number(line.price || 0);
        price += p * Number(line.measurement || 0);
      } else if (line.isNote || line.isFreeMark) {
        // no price
      } else {
        const p = Number(line?.currentProduct?.price || 0);
        price += p * Number(line.measurement || 0);
      }
    });

    const analysis = analyzeJoints(lines);
    const endcapMiterData = {
      endCapsByProduct: analysis.endCapsByProduct,
      mitersByProduct: analysis.mitersByProduct,
      mixedMiters: analysis.mixedMiters,
    };
    console.log("endcapMiterData: ", endcapMiterData);

    const caseIncludes = (hay, needle) =>
      String(hay).toLowerCase().includes(String(needle).toLowerCase());

    // --- local helpers (robust to your seedTemplate.js) ---
    const ci = (s) => String(s || "").toLowerCase();
    const nameHasAny = (n, tokens = []) => {
      const S = ci(n);
      return tokens.some((t) => S.includes(ci(t)));
    };
    const nameTokensForProfile = (profileKey) => {
      const p = ci(profileKey || "");
      if (p.includes("k")) return ["k-style"];
      if (p.includes("straight")) return ["straight face", "straight-face"];
      if (p.includes("half")) return ["half round", "half-round"];
      if (p.includes("box")) return ["box"];
      if (p.includes("round")) return ["round"];
      return [p];
    };
    const inchesToken = (sizeInches) => {
      if (!sizeInches) return null;
      const s = String(sizeInches).replace(/\s+/g, "");
      if (/^\d+"$/.test(s)) return s;
      const m = s.match(/^(\d+)$/);
      return m ? `${m[1]}"` : s;
    };

    // kind: "end cap" | "strip miter" | "bay miter" | "custom miter"
    const findGutterAccessoryTemplate = (
      allProducts,
      { profileKey, sizeInches, kind },
    ) => {
      const sizeTok = inchesToken(sizeInches);
      const profTokens = nameTokensForProfile(profileKey);
      const K = ci(kind);
      const wantEnd = /end/.test(K);
      const wantStrip = /strip/.test(K);
      const wantBay = /bay/.test(K);
      const wantCustom = /custom/.test(K);

      const pass = (requireSize) =>
        (allProducts || []).find((p) => {
          const n = p?.name || "";
          if (!/accessory/i.test(p?.type || "accessory")) return false;
          if (!nameHasAny(n, profTokens)) return false;
          if (requireSize && sizeTok && !n.includes(sizeTok)) return false;
          if (wantEnd && !/end\s*cap|endcap/i.test(n)) return false;
          if (wantStrip && !/strip\s*miter/i.test(n)) return false;
          if (wantBay && !/bay\s*miter/i.test(n)) return false;
          if (wantCustom && !/custom\s*miter/i.test(n)) return false;
          return true;
        });

      return pass(true) || pass(false) || null;
    };

    function calculateEndCapsAndMiters(analysisData) {
      const endCaps = {};
      const miters = {};
      const customMiters = {};
      const accessoryLineItems = [];

      // wrapper so we never forget to include price
      const pushCatalogItem = (product, quantity, meta = {}, nameOverride) => {
        if (!product || !quantity) return;
        accessoryLineItems.push({
          name: nameOverride || product.name,
          quantity: Number(quantity || 0),
          price: Number(product.price || 0),
          product,
          meta,
        });
      };

      // Helper: find the "Custom Miter" product by name (global for any profile/size)
      const findCustomMiterByName = () => {
        const lower = (p) => String(p || "").toLowerCase();
        return (
          (allProducts || []).find(
            (p) =>
              lower(p?.type) === "accessory" &&
              lower(p?.name) === "custom miter",
          ) || null
        );
      };

      // ---------------- End Caps ----------------
      Object.keys(analysisData.endCapsByProduct || {}).forEach((keyLabel) => {
        const endCapCount = Number(
          analysisData.endCapsByProduct[keyLabel] || 0,
        );
        if (!endCapCount) return;

        // Parse size/profile from label like `6" K-Style`
        const { sizeInches, profileKey } = splitSizeAndProfileFromKey(keyLabel);

        const endCapTpl = findGutterAccessoryTemplate(allProducts, {
          profileKey,
          sizeInches, // <-- pass the actual size so we don't default to 5"
          kind: "end cap",
        });

        if (endCapTpl) {
          (endCaps[keyLabel] ||= []).push({
            type: "End Cap",
            price: endCapTpl.price,
            quantity: endCapCount,
            product: endCapTpl,
          });

          pushCatalogItem(endCapTpl, endCapCount, {
            kind: "endcap",
            profileKey,
            sizeInches,
          });
        }
      });

      // ---------------- Miters (strip + bay + custom) ----------------
      Object.keys(analysisData.mitersByProduct || {}).forEach((keyLabel) => {
        const m = analysisData.mitersByProduct[keyLabel] || {};
        const stripQty = Number(m.inside90 || 0) + Number(m.outside90 || 0);
        const bayQty = Number(m.bay135 || 0);

        const { sizeInches, profileKey } = splitSizeAndProfileFromKey(keyLabel);

        if (stripQty > 0) {
          const strip = findGutterAccessoryTemplate(allProducts, {
            profileKey,
            sizeInches, // <-- pass the actual size
            kind: "strip miter",
          });
          if (strip) {
            (miters[keyLabel] ||= []).push({
              type: "Strip Miter",
              price: strip.price,
              quantity: stripQty,
              product: strip,
            });
            pushCatalogItem(strip, stripQty, {
              kind: "miter",
              profileKey,
              miterType: "strip",
              sizeInches,
            });
          }
        }

        if (bayQty > 0) {
          const bay = findGutterAccessoryTemplate(allProducts, {
            profileKey,
            sizeInches, // <-- pass the actual size
            kind: "bay miter",
          });
          if (bay) {
            (miters[keyLabel] ||= []).push({
              type: "Bay 135 Miter",
              price: bay.price,
              quantity: bayQty,
              product: bay,
            });
            pushCatalogItem(bay, bayQty, {
              kind: "miter",
              profileKey,
              miterType: "bay",
              degrees: 135,
              sizeInches,
            });
          }
        }

        // Custom angles
        (Array.isArray(m.custom) ? m.custom : []).forEach(
          ({ angle, count }) => {
            const qty = Number(count || 0);
            if (!qty) return;

            // Always pick the “Custom Miter” product by name, regardless of profile/size
            const customByName = findCustomMiterByName();

            // If not found by name, try the resolver (still pass the real size/profile)
            const custom =
              customByName ||
              findGutterAccessoryTemplate(allProducts, {
                profileKey,
                sizeInches,
                kind: "custom miter",
              }) ||
              findGutterAccessoryTemplate(allProducts, {
                profileKey,
                sizeInches: null, // last resort
                kind: "custom miter",
              });

            if (custom) {
              (customMiters[keyLabel] ||= []).push({
                type: `Custom Miter (${angle}°)`,
                price: custom.price,
                quantity: qty,
                product: custom,
                angle,
              });
              // Display can show angle, product is the generic "Custom Miter"
              pushCatalogItem(
                custom,
                qty,
                {
                  kind: "miter",
                  profileKey,
                  miterType: "custom",
                  degrees: angle,
                  sizeInches,
                },
                `Custom Miter (${angle}°)`,
              );
            }
          },
        );
      });

      return { endCaps, miters, customMiters, accessoryLineItems };
    }

    // --- elbows + offsets (priced) ---
    function collectElbowsAndOffsets(lines, allProducts) {
      // helpers
      const caseIncludes = (hay, needle) =>
        String(hay).toLowerCase().includes(String(needle).toLowerCase());

      function getSizeKeyFromDownspoutLine(line) {
        const fromProp = String(line.downspoutSize || "").trim();
        const rxSize = /(\d+)\s*[xX]\s*(\d+)/;
        const rxQuoted = /(\d+)\s*"?\s*[xX]\s*(\d+)\s*"?/;
        let m = fromProp.match(rxSize) || fromProp.match(rxQuoted);
        if (!m && line.currentProduct?.name) {
          const n = String(line.currentProduct.name || "");
          m = n.match(rxSize) || n.match(rxQuoted);
        }
        if (!m) return "unknown";
        return `${m[1]}x${m[2]}`; // "2x3" / "3x4"
      }

      function inferProfileFromLine(line) {
        const p = String(line.profile || "").toLowerCase();
        if (p) return p;
        const n = String(line.currentProduct?.name || "").toLowerCase();
        if (n.includes("round")) return "round";
        if (n.includes("smooth")) return "smooth";
        if (n.includes("box")) return "box";
        return "corrugated";
      }

      function parseElbowsAndOffsets(seq) {
        const out = { elbows: {}, offsets: {} };
        if (!seq) return out;
        const s = String(seq).trim();

        (s.match(/[A-D]/gi) || []).forEach((ch) => {
          const up = ch.toUpperCase();
          out.elbows[up] = (out.elbows[up] || 0) + 1;
        });

        const consumed = [];
        const unitRe = /(\d+)\s*(?:"|in\b|inch\b|”)/gi;
        let m;
        while ((m = unitRe.exec(s))) {
          const inches = m[1];
          out.offsets[inches] = (out.offsets[inches] || 0) + 1;
          consumed.push([m.index, unitRe.lastIndex]);
        }
        const isConsumed = (idx) =>
          consumed.some(([a, b]) => idx >= a && idx < b);

        for (let i = 0; i < s.length; i++) {
          if (isConsumed(i)) continue;
          const ch = s[i];
          if (/\d/.test(ch)) {
            let j = i;
            let run = "";
            while (j < s.length && /\d/.test(s[j]) && !isConsumed(j)) {
              run += s[j++];
            }
            if (run.length === 1) {
              out.offsets[run] = (out.offsets[run] || 0) + 1;
            } else {
              for (const d of run) {
                out.offsets[d] = (out.offsets[d] || 0) + 1;
              }
            }
            i = j - 1;
          }
        }
        return out;
      }

      const elbowCounts = {};
      const offsetCounts = {};

      lines.forEach((line) => {
        if (!line?.isDownspout) return;

        const sizeKey = getSizeKeyFromDownspoutLine(line);
        const profileKey = inferProfileFromLine(line);
        const mapKey = `${sizeKey}|${profileKey}`;

        const { elbows, offsets } = parseElbowsAndOffsets(
          line.elbowSequence || "",
        );

        if (!elbowCounts[mapKey]) elbowCounts[mapKey] = {};
        Object.entries(elbows).forEach(([code, qty]) => {
          elbowCounts[mapKey][code] =
            (elbowCounts[mapKey][code] || 0) + Number(qty || 0);
        });

        if (!offsetCounts[mapKey]) offsetCounts[mapKey] = {};
        Object.entries(offsets).forEach(([inches, qty]) => {
          offsetCounts[mapKey][inches] =
            (offsetCounts[mapKey][inches] || 0) + Number(qty || 0);
        });
      });

      const items = [];

      // elbows
      Object.entries(elbowCounts).forEach(([key, byCode]) => {
        const [sizeKey, profileKey] = key.split("|");
        Object.entries(byCode).forEach(([code, qty]) => {
          if (!qty) return;

          const prod = findDownspoutFitting(allProducts, {
            profileKey, // corrugated/smooth/box/round
            dsSize: sizeKey, // "2x3" / "3x4" / '3"' ...
            code, // "A" | "B" (ignored for round)
            kind: "elbow",
          });
          if (!prod) return;

          items.push({
            name: `${sizeKey} ${
              /round/i.test(prod.name)
                ? "Round"
                : /smooth/i.test(prod.name)
                  ? "Smooth"
                  : /box/i.test(prod.name)
                    ? "Box"
                    : "Corrugated"
            }${/round|box/i.test(prod.name) ? "" : ` ${code}`} Elbow`,
            quantity: Number(qty || 0),
            price: Number(prod.price || 0),
            product: prod,
            meta: {
              kind: "elbow",
              size: sizeKey,
              code, // keep canonical 'code'
              letter: code, // (back-compat if anything still reads 'letter')
              profileKey,
            },
          });
        });
      });

      // offsets
      Object.entries(offsetCounts).forEach(([key, byInches]) => {
        const [sizeKey, profileKey] = key.split("|");
        Object.entries(byInches).forEach(([inches, qty]) => {
          if (!qty) return;

          const prod =
            findDownspoutFitting(allProducts, {
              profileKey,
              dsSize: sizeKey,
              kind: `${inches}" offset`,
            }) ||
            (allProducts || []).find(
              (p) =>
                caseIncludes(p.name, sizeKey) &&
                caseIncludes(p.name, "offset") &&
                (caseIncludes(p.name, `${inches}"`) ||
                  caseIncludes(p.name, ` ${inches} `)),
            );

          if (!prod) return;

          items.push({
            name: `${sizeKey} ${
              /round/i.test(prod.name)
                ? "Round"
                : /smooth/i.test(prod.name)
                  ? "Smooth"
                  : /box/i.test(prod.name)
                    ? "Box"
                    : "Corrugated"
            } ${inches}" Offset`,
            quantity: Number(qty || 0),
            price: Number(prod.price || 0),
            product: prod,
            meta: {
              kind: "offset",
              size: sizeKey,
              inches: String(inches),
              profileKey,
            },
          });
        });
      });

      return items;
    }

    const { endCaps, miters, customMiters, accessoryLineItems } =
      calculateEndCapsAndMiters(endcapMiterData);

    const elbowOffsetItems = collectElbowsAndOffsets(lines, allProducts);

    // de-duplicate ALL accessories before pricing/rendering
    const allAccessoriesUnfolded = [...accessoryLineItems, ...elbowOffsetItems];
    console.log(accessoryLineItems);
    const allAccessories = foldAccessoryItems(allAccessoriesUnfolded);

    // price from folded list ONLY
    allAccessories.forEach((it) => {
      price += Number(it.price || 0) * Number(it.quantity || 0);
    });

    const rect = canvasRef.current.getBoundingClientRect();

    const isExistingDiagram = Boolean(selectedDiagram?._id);
    const transform = displayTransformRef.current;

    const shouldSaveBackToBaseSpace =
      isExistingDiagram &&
      transform?.diagramId === (selectedDiagram?._id || selectedDiagram?.id) &&
      transform?.baseMeta;

    const linesInSaveSpace = shouldSaveBackToBaseSpace
      ? lines.map(unscaleLineForSave)
      : lines;

    const metaViewport = shouldSaveBackToBaseSpace
      ? {
          ...transform.baseMeta,
          canvasW: transform.baseMeta.canvasW,
          canvasH: transform.baseMeta.canvasH,
          gridSize: transform.baseMeta.gridSize,
          feetPerSquare: transform.baseMeta.feetPerSquare,

          // debug/display-only info
          lastViewportW: Math.round(rect.width),
          lastViewportH: Math.round(rect.height),
          lastDisplayGridSize: gridSize,
        }
      : {
          canvasW: Math.round(rect.width),
          canvasH: Math.round(rect.height),
          gridSize,
          feetPerSquare,
          dpr: window.devicePixelRatio || 1,
          version: 1,
        };

    function normalizeAccessoriesForAPI(items) {
      return (items || []).map((it) => {
        const out = {
          ...it,
          quantity: Number(it.quantity || 0),
          price: Number(it.price || 0),
        };
        const m = { ...(out.meta || {}) };

        // Canonicalize server-side keys
        if (m.kind === "endcap") m.kind = "endCap";
        if (m.kind === "miter") {
          if (!m.type && m.miterType) {
            const t = String(m.miterType);
            m.type = t.charAt(0).toUpperCase() + t.slice(1); // Strip/Bay/Custom
          }
          if (m.degrees != null) m.degrees = Number(m.degrees);
        }
        if (!m.size && m.sizeLabel) m.size = m.sizeLabel;
        if (!m.profileKey && m.profile) m.profileKey = m.profile;
        if (m.code) m.code = String(m.code).toUpperCase();
        if (m.inches != null) m.inches = String(m.inches);

        out.meta = m;

        // Defensive: embed discriminators in name so nothing merges even if some meta is lost downstream.
        if (
          m.kind === "miter" &&
          m.type === "Custom" &&
          m.degrees != null &&
          !/Custom Miter\s*\(\d+°\)/i.test(out.name)
        ) {
          out.name = `Custom Miter (${m.degrees}°)`;
        }
        if (
          m.kind === "elbow" &&
          m.code &&
          !/round/i.test(out.name) &&
          !new RegExp(`\\b${m.code}\\b`, "i").test(out.name)
        ) {
          out.name = out.name.replace(/\bElbow\b/i, `${m.code} Elbow`);
        }
        if (
          m.kind === "offset" &&
          m.inches &&
          !new RegExp(`\\b${m.inches}"\\b`).test(out.name)
        ) {
          out.name = out.name.replace(/\bOffset\b/i, `${m.inches}" Offset`);
        }

        return out;
      });
    }

    const svg = exportDiagramAsSVG(linesInSaveSpace, metaViewport);

    // Convenient data-url form for <img src="..."> when you want it
    const svgDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

    const linesForSave = linesInSaveSpace.map((line) => {
      if (line.isNote || line.isFreeMark) return line;

      if (line.isDownspout) {
        return {
          ...line,
          measurement: Number(line.totalFeet ?? line.measurement ?? 0),
          totalFeet: Number(line.totalFeet ?? line.measurement ?? 0),
        };
      }

      if (line.currentProduct || line.isGutter) {
        const baseGrid =
          Number(metaViewport?.gridSize) || Number(gridSize) || 1;

        const baseFeet =
          Number(metaViewport?.feetPerSquare) || Number(feetPerSquare) || 1;

        const px = calculateDistance(
          [line.startX, line.startY],
          [line.endX, line.endY],
        );

        const measurement = Math.round(px * (baseFeet / baseGrid));

        return {
          ...line,
          measurement,
          runFeet: measurement,
        };
      }

      return line;
    });
    const totalFootageForSave = linesForSave.reduce((sum, line) => {
      if (line.isDownspout || line.isFreeMark || line.isNote) return sum;

      const feet = Number(line.runFeet ?? line.measurement ?? 0);
      return sum + (Number.isFinite(feet) ? feet : 0);
    }, 0);

    const data = {
      lines: linesForSave,
      imageData: thumbnailDataUrl,
      svg,
      totalFootage: totalFootageForSave,
      price: parseFloat(price).toFixed(2),
      // legacy fields
      miterSummary: analysis.miters,
      endCaps: analysis.endCaps,
      endCapsByProduct: analysis.endCapsByProduct,
      mitersByProduct: analysis.mitersByProduct,
      mixedMiters: analysis.mixedMiters,
      accessoryData: [endCaps, miters, customMiters],
      // new
      accessories: {
        items: normalizeAccessoriesForAPI(allAccessories), // folded accessories list
      },
      // persist the canvas size this drawing was made on
      meta: metaViewport,
    };

    console.log("saving data: ", data);
    function handleAddDiagramToProject() {
      addDiagramToProject(resolvedProjectId, token, data)
        .then(async (newDiagramData) => {
          handlePassDiagramData(newDiagramData);

          try {
            await track("diagram_saved", {
              projectId: resolvedProjectId,
              lineCount: Array.isArray(data?.lines) ? data.lines.length : 0,
              totalFootage: data?.totalFootage || null,
            });
          } catch (err) {
            console.warn("Failed to track diagram_saved:", err);
          }

          hardUnlockScroll();
          closeModal();
        })
        .then(() => {
          // FULL reset so the next open starts blank
          setSelectedDiagram({});
          setLines([]);
          setSelectedIndex(null);
          setIsDrawing(false);
          if (baselineHashRef && "current" in baselineHashRef) {
            baselineHashRef.current = hashLines([]);
          }
          clearCanvas();
        })
        .catch((err) => {
          console.error("Failed to save diagram:", err);
          hardUnlockScroll();
          closeModal();
        });
    }

    function handleUpdateDiagram() {
      if (!selectedDiagram?._id) {
        handleAddDiagramToProject();
        return;
      }
      updateDiagram(resolvedProjectId, selectedDiagram._id, token, data)
        .then(async (newDiagramData) => {
          handlePassDiagramData(newDiagramData);

          try {
            await track("diagram_saved", {
              projectId: resolvedProjectId,
              diagramId: selectedDiagram._id,
              lineCount: Array.isArray(data?.lines) ? data.lines.length : 0,
              totalFootage: data?.totalFootage || null,
              overwrite: true,
            });
          } catch (err) {
            console.warn("Failed to track diagram_saved:", err);
          }

          hardUnlockScroll();
          closeModal();
        })
        .then(() => {
          // FULL reset so the next open starts blank
          setSelectedDiagram({});
          setLines([]);
          setSelectedIndex(null);
          setIsDrawing(false);
          if (baselineHashRef && "current" in baselineHashRef) {
            baselineHashRef.current = hashLines([]);
          }
          clearCanvas();
        })
        .catch((err) => {
          /* hide this from regular users */
          console.error("Failed to save diagram:", err);
        });
    }

    if (saveType === "overwrite") {
      handleUpdateDiagram();
    } else {
      handleAddDiagramToProject();
    }

    // after successful save/add/overwrite:
    baselineHashRef.current = hashLines(lines);
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d", { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setLines([]);
    setLineLength(0);
  }

  // ======= UI =======
  if (isLoading) return <div className="diagram">Loading products…</div>;
  if (error) return <div className="diagram">Failed to load products.</div>;

  // product dropdown (native select; colored dot prefix for visibility)
  return (
    <>
      <div
        className={
          [
            "diagram",
            "downspout",
            "selectedLine",
            "confirmDiagramOverwrite",
            "note",
          ].includes(activeModal)
            ? "diagram diagram_visible"
            : "diagram"
        }
      >
        <img
          onClick={() => {
            // hard unlock scroll (in case parent modal state is out of sync)
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
            unlockPageScroll();
            hardUnlockScroll();
            closeModal();
            setSelectedDiagram({});
            setLines([]);
            setSelectedIndex(null);
            setIsDrawing(false);
            if (baselineHashRef && "current" in baselineHashRef) {
              baselineHashRef.current = hashLines([]);
            }
          }}
          src={closeIcon}
          alt="close diagram"
          className="diagram__close diagram__icon"
        />

        <img
          src={saveIcon}
          alt="save diagram"
          className="diagram__icon diagram__save"
          data-onboarding="diagram-save-button"
          onClick={() => {
            setLines((prev) =>
              prev.map((l) => (l.isSelected ? { ...l, isSelected: false } : l)),
            );
            try {
              const nextHash = hashLines(lines);
              const hasChanged = nextHash !== baselineHashRef.current;

              // Existing diagram changed -> open confirm modal
              if (selectedDiagram?._id && hasChanged) {
                setActiveModal("confirmDiagramOverwrite");
                return;
              }

              // New diagram or no change -> follow your existing save path
              // If you already have a function, call it here (e.g., saveDiagram(), handleSave(), etc.)
              if (typeof saveDiagram === "function") {
                saveDiagram();
              } else if (typeof handleSave === "function") {
                handleSave();
              } else {
                // fallback: if you were previously just closing without saving
                // leave this empty or implement your minimal persist
              }

              // Update baseline so next edits are compared properly
              baselineHashRef.current = nextHash;

              // Close or keep modal as your current flow dictates
              // setActiveModal(null); // uncomment if your flow closes after save
            } catch (err) {
              console.error("Save diagram failed:", err);
            }
          }}
        />

        <img
          src={trashIcon}
          alt="clear or delete"
          className="diagram__icon diagram__trash"
          onClick={() => {
            if (tool === "select" && selectedIndex !== null) deleteSelected();
            else clearCanvas();
          }}
        />

        {/* <img
          src={itemsIcon}
          alt="select product"
          className="diagram__icon diagram__items"
          onClick={() => {}}
        /> */}

        {/*  <select
          value={tool}
          onChange={handleToolSelectChange}
          className="diagram__select-product"
          name="select product dropdown"
          id="select-product-dropdown"
        >
          {filteredProducts?.map((p) => {
            const bg = p.colorCode || p.visual || p.color || "#ffffff";
            const label = `● ${p.name}`;
            return (
              <option key={p._id} value={p.name} style={{ color: bg }}>
                {label}
              </option>
            );
          })}
          <option value="downspout">Downspout</option>
          <option value="select">Select</option>
          <option value="note">Notation</option>
          <option value="splashGuard">Splash Guard / Valley Shield</option>
          <option value="freeLine">Free Line</option>
        </select */}
        {/* <div
          style={{
            display: "flex",
            overflowX: "auto",
            gap: "8px",
            padding: "8px",
            borderBottom: "1px solid #ddd",
            background: "#fff",
          }}
        >
          {drawTools.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setTool(item.value);
                setActiveTool(item);
              }}
              style={{
                minWidth: "90px",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                background: tool === item.value ? "#1976d2" : "#f7f7f7",
                color: tool === item.value ? "white" : "black",
                fontSize: "14px",
              }}
            >
              {item.label}
            </button>
          ))}

          <button
            type="button"
            onClick={() => {
              setTool("downspout");
              setActiveTool(null);
            }}
            style={{
              minWidth: "90px",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              background: tool === "downspout" ? "#1976d2" : "#f7f7f7",
              color: tool === "downspout" ? "white" : "black",
              fontSize: "14px",
            }}
          >
            Downspout
          </button>

          <button
            type="button"
            onClick={() => {
              setTool("select");
              setActiveTool(null);
            }}
            style={{
              minWidth: "90px",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              background: tool === "select" ? "#1976d2" : "#f7f7f7",
              color: tool === "select" ? "white" : "black",
              fontSize: "14px",
            }}
          >
            Select
          </button>

          <button
            type="button"
            onClick={() => {
              setTool("note");
              setActiveTool(null);
            }}
            style={{
              minWidth: "90px",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              background: tool === "note" ? "#1976d2" : "#f7f7f7",
              color: tool === "note" ? "white" : "black",
              fontSize: "14px",
            }}
          >
            Note
          </button>
        </div> */}
        <div
          className="diagram__toolbar"
          style={{
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            overscrollBehaviorX: "contain",
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="diagram__toolbar-section">
            <div className="diagram__toolbar-label">Actions</div>

            <div className="diagram__toolbar-row">
              {[
                { value: "select", label: "Select" },
                { value: "downspout", label: "Downspout" },
                { value: "note", label: "Note" },
                { value: "freeLine", label: "Free Draw" },
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    setTool(item.value);
                    setActiveTool(null);
                  }}
                  className={
                    tool === item.value
                      ? "diagram__tool-button diagram__tool-button_active"
                      : "diagram__tool-button"
                  }
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {drawTools.length > 0 && (
            <div className="diagram__toolbar-section">
              <div className="diagram__toolbar-label">Draw Items</div>

              <div className="diagram__toolbar-row">
                {drawTools.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setTool(item.value);
                      setActiveTool(item);
                    }}
                    className={
                      tool === item.value
                        ? "diagram__tool-button diagram__tool-button_active"
                        : "diagram__tool-button"
                    }
                  >
                    <span
                      className="diagram__tool-swatch"
                      style={{ backgroundColor: item.color || "#000" }}
                    />
                    <span className="diagram__tool-text">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {assemblyTools.length > 0 && (
            <div className="diagram__toolbar-section">
              <div className="diagram__toolbar-label">Assemblies</div>

              <div className="diagram__toolbar-row">
                {assemblyTools.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setTool(item.value);
                      setActiveTool(item);
                    }}
                    className={
                      tool === item.value
                        ? "diagram__tool-button diagram__tool-button_active diagram__tool-button_assembly"
                        : "diagram__tool-button diagram__tool-button_assembly"
                    }
                  >
                    <span
                      className="diagram__tool-swatch"
                      style={{ backgroundColor: item.color || "#000" }}
                    />
                    <span className="diagram__tool-text">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {(tool === "freeLine" ||
          (selectedIndex !== null && lines[selectedIndex]?.isFreeMark)) && (
          <div className="diagram__floating-panel diagram__free-tools-panel">
            {/* Shape (with filled/hollow choices baked in) */}
            <label className="diagram__floating-panel-row">
              <span>Shape</span>
              <select
                className="diagram__panel-select"
                value={(() => {
                  const src =
                    selectedIndex !== null && lines[selectedIndex]?.isFreeMark
                      ? lines[selectedIndex]
                      : currentLine;
                  const kind = src.kind || "free-line";
                  const filled = !!src.fill;
                  if (kind === "free-square")
                    return filled ? "free-square:filled" : "free-square:hollow";
                  if (kind === "free-circle")
                    return filled ? "free-circle:filled" : "free-circle:hollow";
                  return "free-line";
                })()}
                onChange={(e) => {
                  const raw = e.target.value; // e.g., "free-circle:filled" | "free-square:hollow" | "free-line"
                  let nextKind = "free-line";
                  let nextFill;

                  if (raw.startsWith("free-circle")) {
                    nextKind = "free-circle";
                    nextFill = raw.endsWith(":filled");
                  } else if (raw.startsWith("free-square")) {
                    nextKind = "free-square";
                    nextFill = raw.endsWith(":filled");
                  } else {
                    nextKind = "free-line";
                  }

                  if (
                    selectedIndex !== null &&
                    lines[selectedIndex]?.isFreeMark
                  ) {
                    setLines((prev) => {
                      const copy = [...prev];
                      const el = { ...copy[selectedIndex] };
                      el.kind = nextKind;
                      if (nextKind !== "free-line") el.fill = !!nextFill;
                      else delete el.fill;
                      if (nextKind === "free-circle" && el.radius == null) {
                        el.radius = 12;
                        el.centerX = el.centerX ?? el.startX ?? 0;
                        el.centerY = el.centerY ?? el.startY ?? 0;
                      }
                      copy[selectedIndex] = el;
                      return copy;
                    });
                  } else {
                    setCurrentLine((prev) => {
                      const el = { ...prev, kind: nextKind };
                      if (nextKind !== "free-line") el.fill = !!nextFill;
                      else delete el.fill;
                      if (nextKind === "free-circle" && el.radius == null) {
                        el.radius = 12;
                        el.centerX = el.centerX ?? el.startX ?? 0;
                        el.centerY = el.centerY ?? el.startY ?? 0;
                      }
                      return el;
                    });
                  }
                }}
                style={{
                  padding: "2px 6px",
                  background: "#1f1f22",
                  color: "#eaeaea",
                  border: "1px solid #333",
                  borderRadius: 6,
                }}
              >
                <option value="free-line">Line</option>
                <option value="free-square:hollow">Square (Hollow)</option>
                <option value="free-square:filled">Square (Filled)</option>
                <option value="free-circle:hollow">Circle (Hollow)</option>
                <option value="free-circle:filled">Circle (Filled)</option>
              </select>
            </label>
            {/* Style (solid / dotted) */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
              }}
            >
              <span>Style</span>
              <select
                value={
                  selectedIndex !== null && lines[selectedIndex]?.isFreeMark
                    ? lines[selectedIndex].dashed
                      ? "dotted"
                      : "solid"
                    : currentLine.dashed
                      ? "dotted"
                      : "solid"
                }
                className="diagram__panel-select"
                onChange={(e) => {
                  const val = e.target.value === "dotted";
                  if (
                    selectedIndex !== null &&
                    lines[selectedIndex]?.isFreeMark
                  ) {
                    setLines((prev) => {
                      const copy = [...prev];
                      copy[selectedIndex] = {
                        ...copy[selectedIndex],
                        dashed: val,
                      };
                      return copy;
                    });
                  } else {
                    setCurrentLine((prev) => ({ ...prev, dashed: val }));
                  }
                }}
                style={{
                  padding: "2px 6px",
                  background: "#1f1f22",
                  color: "#eaeaea",
                  border: "1px solid #333",
                  borderRadius: 6,
                }}
              >
                <option value="solid">Solid</option>
                <option value="dotted">Dotted</option>
              </select>
            </label>

            {/* Color swatches */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12 }}>Color</span>
              <div lassName="diagram__color-row">
                {COMMON_COLORS.map((c) => {
                  const selectedColor =
                    selectedIndex !== null && lines[selectedIndex]?.isFreeMark
                      ? lines[selectedIndex].color || "#111111"
                      : currentLine.color || "#111111";
                  const isSelected =
                    selectedColor.toLowerCase() === c.toLowerCase();
                  return (
                    <button
                      key={c}
                      onClick={() => {
                        if (
                          selectedIndex !== null &&
                          lines[selectedIndex]?.isFreeMark
                        ) {
                          setLines((prev) => {
                            const copy = [...prev];
                            copy[selectedIndex] = {
                              ...copy[selectedIndex],
                              color: c,
                            };
                            return copy;
                          });
                        } else {
                          setCurrentLine((prev) => ({ ...prev, color: c }));
                        }
                      }}
                      title={c}
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: isSelected
                          ? "2px solid #eaeaea"
                          : "1px solid #555",
                        background: c,
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {(lines?.length ?? 0) === 0 && (
          <div className="diagram__floating-panel diagram__grid-settings">
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span>Grid square size (px):</span>
              <input
                type="number"
                min={4}
                step={1}
                value={gridSize}
                onChange={(e) => {
                  const v = Math.round(
                    Math.max(2, Number(e.target.value)) || 10,
                  );
                  setGridSize(v);
                }}
                style={{ width: 90, padding: "4px 6px", color: "white" }}
                className="diagram__panel-input"
              />
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span>Feet per square:</span>
              <input
                type="number"
                min={1}
                step={1}
                value={feetPerSquare}
                onChange={(e) => {
                  const v = Math.round(
                    Math.max(0.01, Number(e.target.value)) || 1,
                  );
                  setFeetPerSquare(v);
                }}
                style={{ width: 90, padding: "4px 6px", color: "white" }}
                className="diagram__panel-input"
              />
            </label>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="diagram__canvas"
          /*  width={window.innerWidth}
          height={window.innerHeight} */
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onPointerDown={handleMouseDown}
          onPointerMove={handleMouseMove}
          onPointerUp={handleMouseUp}
        />
      </div>

      <DownspoutModal
        setActiveModal={setActiveModal}
        activeModal={activeModal}
        setTool={setTool}
        setIsDownspoutModalOpen={setIsDownspoutModalOpen}
        addDownspout={handleAddDownspout}
        mode={editingDownspoutIndex !== null ? "edit" : "create"}
        initialData={
          editingDownspoutIndex !== null ? lines[editingDownspoutIndex] : null
        }
      />
      <OverwriteDiagramModal
        activeModal={activeModal}
        setActiveModal={setActiveModal}
        saveDiagram={saveDiagram}
        closeModal={closeModal}
      />
      <AnnotationModal
        activeModal={activeModal}
        setActiveModal={(v) => {
          // if closing the modal, clear edit state
          if (v !== "note") setEditingNoteIndex(null);
          setActiveModal(v);
        }}
        mode={editingNoteIndex !== null ? "edit" : "create"}
        initialText={
          editingNoteIndex !== null ? lines[editingNoteIndex]?.note || "" : ""
        }
        onSubmit={(text) => {
          if (editingNoteIndex !== null) {
            // update existing note text
            setLines((prev) => {
              const next = [...prev];
              if (next[editingNoteIndex]) {
                next[editingNoteIndex] = {
                  ...next[editingNoteIndex],
                  note: text,
                };
              }
              return next;
            });
            setEditingNoteIndex(null);
            setActiveModal("diagram");
          } else {
            // existing create flow
            addNote(text);
            setActiveModal("diagram");
          }
        }}
      />
    </>
  );
};

// ===== SVG Export (additive, non-breaking) =====
export function exportDiagramAsSVG(lines, meta) {
  const W = Number(meta?.canvasW || 0) || 1000;
  const H = Number(meta?.canvasH || 0) || 800;
  const grid = Number(meta?.gridSize || 10) || 10;

  const esc = (s) =>
    String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&apos;");

  const out = [];
  out.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" shape-rendering="geometricPrecision" vector-effect="non-scaling-stroke">`,
  );

  // If you ever want a background like your canvas, uncomment:
  // out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>`);

  for (const l of lines || []) {
    if (!l) continue;

    const x1 = Number(l.startX || 0);
    const y1 = Number(l.startY || 0);
    const x2 = Number(l.endX ?? l.startX ?? 0);
    const y2 = Number(l.endY ?? l.startY ?? 0);

    const stroke = esc(l.color || "#000000");
    const sw = Math.max(1, Number(l.lineWidth || (l.isDownspout ? 2 : 3)));

    // ---- Notes / annotations (text) ----
    if (l.isNote && l.noteText) {
      // matches your canvas style: Arial, bold-ish, black
      // Canvas baseline differs from SVG; this is "close enough" visually.
      out.push(
        `<text x="${x1}" y="${y1}" font-family="Arial" font-size="12" font-weight="700" fill="#000000" text-anchor="middle" dominant-baseline="middle">${esc(
          l.noteText,
        )}</text>`,
      );
      continue;
    }

    // ---- Splash Guard mark (filled circle) ----
    if (l.isSplashGuard) {
      const r = Math.max(2, grid / 2.5);
      out.push(
        `<circle cx="${x1}" cy="${y1}" r="${r}" fill="${stroke}" stroke="${stroke}" stroke-width="1" />`,
      );
      continue;
    }

    // ---- Free marks (line / square / circle) ----
    if (l.isFreeMark) {
      const type = String(l.freeType || "");
      const dashed = !!l.isDashed;
      const dash = dashed ? ` stroke-dasharray="${Math.max(2, sw * 2)}"` : "";

      if (type === "line") {
        out.push(
          `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"${dash} />`,
        );
        continue;
      }

      if (type === "square") {
        const s = Math.max(6, Number(l.size || grid * 1.5));
        const half = s / 2;
        const fill = l.isFilled ? stroke : "none";
        out.push(
          `<rect x="${x1 - half}" y="${
            y1 - half
          }" width="${s}" height="${s}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"${dash} />`,
        );
        continue;
      }

      if (type === "circle") {
        const r = Math.max(4, Number(l.size || grid * 1.5) / 2);
        const fill = l.isFilled ? stroke : "none";
        out.push(
          `<circle cx="${x1}" cy="${y1}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}"${dash} />`,
        );
        continue;
      }

      // unknown free mark fallback
      out.push(
        `<circle cx="${x1}" cy="${y1}" r="${Math.max(
          3,
          grid / 3,
        )}" fill="${stroke}" />`,
      );
      continue;
    }

    // ---- Downspout (X + draggable label box) ----
    if (l.isDownspout) {
      const d = grid / 2.75;

      // X mark (matches your drawDownspoutX)
      out.push(
        `<line x1="${x1}" y1="${y1}" x2="${x1 + d}" y2="${
          y1 + d
        }" stroke="${stroke}" stroke-width="2" />`,
      );
      out.push(
        `<line x1="${x1}" y1="${y1}" x2="${x1 - d}" y2="${
          y1 + d
        }" stroke="${stroke}" stroke-width="2" />`,
      );
      out.push(
        `<line x1="${x1}" y1="${y1}" x2="${x1 - d}" y2="${
          y1 - d
        }" stroke="${stroke}" stroke-width="2" />`,
      );
      out.push(
        `<line x1="${x1}" y1="${y1}" x2="${x1 + d}" y2="${
          y1 - d
        }" stroke="${stroke}" stroke-width="2" />`,
      );

      // Box (matches your drawDownspoutBox coords + styling)
      const x = Number(l.downspoutBoxX ?? x1 + grid * 0.8);
      const y = Number(l.downspoutBoxY ?? y1 - grid * 2.7);
      const w = Number(l.downspoutBoxW ?? grid * 4.0);
      const h = Number(l.downspoutBoxH ?? grid * 3.5);

      out.push(
        `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="#444444" stroke="${stroke}" stroke-width="2" />`,
      );

      // Text (matches your fillText)
      const label1 = l.downspoutSize || "Downspout";
      const label2 =
        l.measurement != null && l.measurement !== ""
          ? `${l.measurement} ft`
          : "";

      out.push(
        `<text x="${x + w / 2}" y="${
          y + h / 2.3
        }" font-family="Arial" font-size="12" font-weight="700" fill="#ffffff" text-anchor="middle" dominant-baseline="middle">${esc(
          label1,
        )}</text>`,
      );

      if (label2) {
        out.push(
          `<text x="${x + w / 2}" y="${
            y + (h * 2) / 3
          }" font-family="Arial" font-size="12" font-weight="700" fill="#ffffff" text-anchor="middle" dominant-baseline="middle">${esc(
            label2,
          )}</text>`,
        );
      }

      continue;
    }

    // ---- Normal gutter lines (and any other straight strokes) ----
    const dashed = !!l.isDashed;
    const dash = dashed ? ` stroke-dasharray="${Math.max(2, sw * 2)}"` : "";
    out.push(
      `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"${dash} />`,
    );
  }

  out.push(`</svg>`);
  return out.join("");
}

export default Diagram;
